import { useContext, useEffect, useRef, useState, useCallback } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { ChatbotUIChatContext, ScopedChatBindings } from '@/context/chat'
import { ChatbotUIContext } from '@/context/context'
import {
  captureClientException,
  capturePublicChatFirstResponseException
} from '@/lib/analytics/capture-client-exception'
import { getAssistantCollectionsByAssistantId } from '@/db/assistant-collections'
import { getAssistantFilesByAssistantId } from '@/db/assistant-files'
import { getAssistantToolsByAssistantId } from '@/db/assistant-tools'
import { createFile, createFileFromStoragePath } from '@/db/files'
import {
  deleteChat as deleteChatInDB,
  touchChatActivity,
  updateChat as updateChatInDB
} from '@/db/chats'
import { getCollectionFilesByCollectionId } from '@/db/collection-files'
import { createMessage, deleteMessage, deleteMessagesIncludingAndAfter, updateMessage } from '@/db/messages'
import { Tables, Json } from '@/supabase/types'
import { ChatFile, ChatMessage, ChatPayload, ChatSettings, LLMID, MessageImage } from '@/types'
import type {
  ChatAdjacentPaneContext,
  ChatLinkedSpaceContext,
  ChatSendOptions,
  ChatStopOptions,
  ChatStructuredMention,
  ChatStructuredMentionKind,
  ChatStructuredMentions
} from '@/types/chat'
import { ToolTimelineEntry } from '@/types/annotation'
import { t } from 'i18next'
import { useAtom } from 'jotai'
import { toast } from 'sonner'
import { stripMentionMarkupForPrompt } from '@/lib/chat/mentions'
import { realtimeActiveChatIdAtom } from '@/atoms/chatAtoms'
import { addChatAtom, updateChatAtom } from '@/atoms/workspaceAtoms'

import { SubscriptionRequiredError } from '@/lib/errors'
import { guessFileExtensionByContentType } from '@/lib/content-type'
import type { DesktopHandoffAttachment } from '@/lib/desktop-host'
// import { processChatMemories } from "@/lib/memory/process-chat-memories"
import {
  clearChatActivityStatus,
  createChatActivityStatus,
  readChatActivityRegistry,
  upsertChatActivityStatus,
  writeChatActivityRegistry
} from '@/lib/chat/client-activity-registry'
import {
  beginChatFinalize,
  waitForChatFinalize
} from '@/lib/chat/chat-finalize-queue'
import {
  hasActiveSendRequestActivity,
  isSendLockedByActiveRequest
} from '@/lib/chat/send-request-lock'
import { validateChatDraftAttachmentUpload } from '@/lib/chat/attachment-upload-limits'
import { getNonBlobImageSource } from '@/lib/chat/message-image-source'
import {
  buildPendingAssistantTurnMetadata,
  failPendingAssistantTurnMetadata,
  completePendingAssistantTurnMetadata,
  getLatestPendingAssistantTurnState,
  stopPendingAssistantTurnMetadata,
  stopStalePendingAssistantTurns
} from '@/lib/chat/pending-assistant-turn'
import { sanitizeAssistantVisibleContent } from '@/lib/chat/assistant-visible-content'
import { readFreeLosiMaxLimitDetails } from '@/lib/chat/free-losi-max-limit-notice'
import { SIGNPOSTING_LOCATION_EVENT } from '@/lib/signposting/events'
import { COMPUTER_USE_CREDENTIAL_EVENT } from '@/lib/computer-use/credential-events'
import {
  CONNECTOR_CONNECTED_EVENT,
  connectorAckMessage,
  type ConnectorConnectedEventDetail
} from '@/lib/connectors/events'

// Module-level dedup: ensures only one of the 20+ useChatHandler
// instances processes any given signposting location event
const _signpostingSentKeys = new Set<string>()

import { stripBlobUrlsFromOutgoingContent } from '@/lib/chat/outgoing-message-content'
import {
  buildTryTempFilesPromptBlock,
  clearAllTryTempFileContents,
  isTryTempFileId
} from '@/lib/chat/try-temp-attachments'
import { mergeVoiceMemoMetadata } from '@/lib/chat/voice-memo'
import { reconstructContentWithCodeBlocksInChatMessage } from '@/lib/messages'
import { isMobileScreen } from '@/lib/mobile'
import { shouldPreserveComputerInteractionFocus } from '@/lib/chat/computer-interaction-focus'
import { getRealtimeClient, initializeRealtimeClient } from '@/lib/realtime-api-client'
import { safeConsole } from '@/lib/utils/safeConsole'
import { authenticatedFetch } from '@/lib/auth/authenticated-fetch'
import { stripAssistantResponseControlMarkers } from '@/lib/utils/parse-suggestions'
import {
  getActivationErrorType,
  getActivationSurfaceFromPath,
  trackChatMessageSent,
  trackChatFirstAiResponseFailed,
  trackChatFirstAiResponseStarted,
  trackChatFirstAiResponseSucceeded,
  trackChatFirstUserMessageSubmitted,
  trackChatSecondUserMessageSubmitted,
  trackFirstChatMessageSent,
  type ChatMessageSentProperties
} from '@/lib/analytics/activation'
import type { ActivationMessageSource } from '@/lib/analytics/activation-event-contract'
import {
  isGroupChatAssistantMentioned,
  isGroupChatMentionOnlyConflictError,
  isGroupChatResponseMode,
  shouldSkipAlwaysModeGroupAssistantForHumanMention,
  shouldSkipGroupChatAssistantResponse
} from '@/lib/chat/group-chat-response-mode'
import { shouldScheduleFirstResponseTitleGeneration } from './title-generation-policy'
import { useCodeBlockManager } from '@/hooks/useCodeBlockManager'
import { useServerEvents } from '@/hooks/useServerEvents'
import { isFreePlan, scheduleDelayedPaywall } from '@/lib/subscription'
import { LLM_LIST } from '@/lib/models/llm/llm-list'
import { showFreeLosiMaxLimitToast } from '../free-losi-max-limit-toast'

import {
  createTempMessages,
  createPendingAssistantMessage,
  generateChatName,
  getPendingAssistantTurnCleanup,
  getRequestScopedPendingTurnCleanup,
  handleCreateChat,
  handleCreateMessages,
  handleHostedChat,
  handleLocalChat,
  handleRetrieval,
  handleToolsChat,
  hasAssistantTurnOutput,
  getMessageImageFromStorageWithTimeout,
  mergeAssistantMessageAnnotation,
  buildChatImagePathFingerprint,
  prepareToolsChatRequest,
  resolveAssistantAnnotationForPersist,
  validateChatSettings,
  warmToolsChatSendCaches,
  type PreparedToolsChatRequest
} from '../chat-helpers'

import { normalizeMessageImageStorageSource } from '@/lib/media/safe-url'
import {
  useScopedAttachmentKey,
  useScopedSetUserInput
} from './use-scoped-user-input'

import { v4 as uuidv4 } from 'uuid'

interface UseChatHandlerProps {
  onChatCreate?: (chat: Tables<'chats'>) => void
  onChatUpdate?: (chat: Tables<'chats'>) => void
  chatId?: string
  forceDraftChat?: boolean
  enableRealtimeFallback?: boolean
  canFocusInput?: boolean
  adjacentPaneContext?: ChatAdjacentPaneContext | null
}

type PendingChatRequestState = {
  input: string
  hasOutput: boolean
  restoreInputOnAbort: boolean
}

function isWebBrowsingTool(tool: Tables<'tools'>) {
  const name = tool.name?.toLowerCase() ?? ''

  return (
    tool.name === 'Web Browsing' ||
    tool.name === 'Web Browsing Tool' ||
    tool.name === 'web_browsing' ||
    (name.includes('web') && name.includes('brows'))
  )
}

function hasAssistantResponsePayload(
  content: string,
  annotations: Json | null,
  metadata?: Json | null
) {
  if (sanitizeAssistantVisibleContent(content).trim().length > 0) return true

  if (annotations && typeof annotations === 'object' && !Array.isArray(annotations)) {
    if (Object.keys(annotations).length > 0) {
      return true
    }
  }

  if (metadata && typeof metadata === 'object' && !Array.isArray(metadata)) {
    return Boolean(
      Array.isArray((metadata as Record<string, unknown>).suggestions) ||
        (metadata as Record<string, unknown>).questionnaire
    )
  }

  return false
}

const isDesktopHandoffImage = (attachment: DesktopHandoffAttachment) =>
  attachment.kind === 'image' || attachment.mimeType.startsWith('image/')

const getErrorMessage = (error: unknown) => {
  const raw =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : ''
  const trimmed = raw.trim()
  if (!trimmed) return ''
  // Never surface internal secret/env names or bare HTTP status lines.
  if (/VOICE_SERVER_SECRET|server config/i.test(trimmed)) {
    return "Check your connection and try again."
  }
  if (/^(HTTP\s*)?\d{3}(\s+[A-Za-z].*)?$/i.test(trimmed)) {
    return ''
  }
  return trimmed
}

const getChatMessageSentSurface = (
  activationSurface: ReturnType<typeof getActivationSurfaceFromPath>
): ChatMessageSentProperties['surface'] => {
  if (activationSurface === 'public_chat') return 'public_chat'
  if (activationSurface === 'authenticated_chat') return 'chat'
  if (activationSurface === 'space') return 'spaces'
  if (activationSurface === 'sidekick') return 'sidekick'
  if (activationSurface === 'embed') return 'embed'
  if (activationSurface === 'nexus') return 'nexus'

  return 'unknown'
}

const mergeMessageImages = (currentImages: MessageImage[], incomingImages: MessageImage[]) => {
  const nextImages = [...currentImages]

  for (const image of incomingImages) {
    const imageKey = image.path || image.url || image.base64 || image.messageId
    const exists = nextImages.some((existingImage) => {
      const existingKey =
        existingImage.path || existingImage.url || existingImage.base64 || existingImage.messageId
      return existingKey === imageKey
    })

    if (!exists) {
      nextImages.push(image)
    }
  }

  return nextImages
}

const getPromptImageSource = (image: MessageImage) => {
  const candidates: unknown[] = [image.base64, image.url, image.path]

  for (const candidate of candidates) {
    const source = normalizeMessageImageStorageSource(candidate)

    if (source && !source.startsWith('blob:')) {
      return source
    }
  }

  return null
}

const buildPromptImagePaths = (images: MessageImage[]) =>
  images
    .map(getPromptImageSource)
    .filter((path): path is string => Boolean(path))

const mergePersistedPromptImagePaths = (
  persistedPaths: string[],
  fallbackPaths: string[],
  imageCount: number
) => {
  const paths: string[] = []

  for (let index = 0; index < imageCount; index++) {
    const path = persistedPaths[index] || fallbackPaths[index]

    if (path) {
      paths.push(path)
    }
  }

  return paths
}

const getModelImagePreviewSource = (image: MessageImage) => {
  const candidates: unknown[] = [image.url, image.base64]

  for (const candidate of candidates) {
    const source = getNonBlobImageSource(candidate)

    if (source) {
      return source
    }
  }

  return null
}

const bindImagesToMessage = (
  images: MessageImage[],
  messageId: string,
  imagePaths: string[]
) =>
  images.flatMap((image, index) => {
    const path = imagePaths[index] ?? getPromptImageSource(image)

    if (!path) {
      return []
    }

    const previewSource = getModelImagePreviewSource(image)

    return [
      {
        ...image,
        messageId,
        path,
        base64: previewSource?.startsWith('data:') ? previewSource : '',
        url: previewSource || path
      }
    ]
  })

const buildRetainedMessageImages = (
  message: ChatMessage['message'] | null | undefined
): MessageImage[] => {
  if (!message || !Array.isArray(message.image_paths)) {
    return []
  }

  return message.image_paths
    .map((path) => normalizeMessageImageStorageSource(path))
    .filter((path): path is string => Boolean(path))
    .map((path) => ({
      messageId: message.id,
      path,
      base64: '',
      url: path,
      file: null
    }))
}

const mergeChatFiles = (currentFiles: ChatFile[], incomingFiles: ChatFile[]) => {
  const nextFiles = [...currentFiles]

  for (const file of incomingFiles) {
    if (!nextFiles.some((existingFile) => existingFile.id === file.id)) {
      nextFiles.push(file)
    }
  }

  return nextFiles
}

const buildDesktopHandoffUserMetadata = (
  attachments: DesktopHandoffAttachment[]
): Json | null => {
  if (attachments.length === 0) {
    return null
  }

  return {
    attachments: attachments.map((attachment) => ({
      id: attachment.id,
      name: attachment.name,
      type: attachment.type,
      mimeType: attachment.mimeType,
      size: attachment.size,
      bucket: attachment.bucket,
      path: attachment.path,
      kind: attachment.kind,
      signedUrl: attachment.signedUrl
    }))
  } as Json
}

const buildDesktopHandoffImages = (attachments: DesktopHandoffAttachment[]): MessageImage[] =>
  attachments.filter(isDesktopHandoffImage).map((attachment) => ({
    messageId: attachment.id || uuidv4(),
    path: attachment.path,
    base64: attachment.signedUrl,
    url: attachment.signedUrl,
    file: null
  }))

const inferDesktopHandoffFileType = (
  attachment: DesktopHandoffAttachment,
  file?: Pick<File, 'type'> | null
) => {
  const guessedType = guessFileExtensionByContentType(
    file?.type || attachment.mimeType || attachment.type || 'application/octet-stream',
    attachment.name
  )

  if (guessedType && guessedType !== 'octet-stream') {
    return guessedType
  }

  return attachment.name.split('.').pop()?.toLowerCase() || 'file'
}

function getSidecarRunIdFromAnnotation(annotation: Record<string, unknown>): string | null {
  const sidecarRun = annotation.sidecar_run
  if (sidecarRun && typeof sidecarRun === 'object' && !Array.isArray(sidecarRun)) {
    const runId = (sidecarRun as Record<string, unknown>).runId
    if (typeof runId === 'string' && runId.trim()) return runId
  }

  const continuation = annotation.sidecar_continuation
  if (continuation && typeof continuation === 'object' && !Array.isArray(continuation)) {
    const runId = (continuation as Record<string, unknown>).runId
    if (typeof runId === 'string' && runId.trim()) return runId
  }

  return null
}

function getMessageSidecarRunId(message: ChatMessage): string | null {
  const annotation =
    message.message.annotation && typeof message.message.annotation === 'object'
      ? (message.message.annotation as Record<string, unknown>)
      : null

  return annotation ? getSidecarRunIdFromAnnotation(annotation) : null
}

function findAssistantMessageIndexForToolEvent(
  messages: ChatMessage[],
  toolId?: string,
  toolName?: string
): number {
  const isSidecarTool =
    toolName === 'Sidecar__delegate' ||
    (typeof toolId === 'string' && toolId.startsWith('sidecar-'))

  if (!isSidecarTool) {
    return messages.findLastIndex((message) => message.message.role === 'assistant')
  }

  if (toolId) {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const candidate = messages[index]
      if (candidate.message.role !== 'assistant') continue
      if (getMessageSidecarRunId(candidate) === toolId) {
        return index
      }
    }
  }

  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const candidate = messages[index]
    if (candidate.message.role !== 'assistant') continue

    const content =
      typeof candidate.message.content === 'string'
        ? candidate.message.content.trim()
        : ''
    const candidateAnnotation =
      candidate.message.annotation && typeof candidate.message.annotation === 'object'
        ? (candidate.message.annotation as Record<string, unknown>)
        : {}

    if (!content || candidateAnnotation.sidecar_continuation || candidateAnnotation.sidecar_run) {
      return index
    }
  }

  return messages.findLastIndex((message) => message.message.role === 'assistant')
}

function throwAbortErrorIfNeeded(signal: AbortSignal) {
  if (!signal.aborted) {
    return
  }

  const error = new Error('Request aborted')
  error.name = 'AbortError'
  throw error
}

async function fetchAccessibleChatForSend(chatId: string) {
  const response = await authenticatedFetch(`/api/chat/${encodeURIComponent(chatId)}`, {
    cache: 'no-store',
    credentials: 'include'
  })

  if (!response.ok) {
    return null
  }

  return (await response.json()) as Tables<'chats'>
}

function normalizeChatForClient(chat: Tables<'chats'>): Tables<'chats'> {
  return {
    ...chat,
    metadata: chat.metadata ?? null,
    type: chat.type ?? null
  }
}

function buildLinkedSpaceChatContext(
  chat: Tables<'chats'> | null
): ChatLinkedSpaceContext | null {
  if (!chat?.id || !chat.space_id || !chat.workspace_id) {
    return null
  }

  return {
    chatId: chat.id,
    workspaceId: chat.workspace_id,
    spaceId: chat.space_id
  }
}

function canUseWorkspaceAttachmentUploadLimits(chat: Tables<'chats'> | null) {
  return Boolean(
    chat?.is_group_chat ||
      chat?.space_id ||
      chat?.type === 'space_conversation'
  )
}

function getStoredGroupChatResponseMode(metadata: Json | null | undefined) {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
    return null
  }

  const groupChat = (metadata as Record<string, unknown>).groupChat
  if (!groupChat || typeof groupChat !== 'object' || Array.isArray(groupChat)) {
    return null
  }

  const responseMode = (groupChat as Record<string, unknown>).responseMode
  return isGroupChatResponseMode(responseMode) ? responseMode : null
}

const STRUCTURED_MENTION_PATTERN = /@?\[([^\]]+)\]\(([^:()\s]+):([^)]+)\)/g
const MEMBER_MENTION_TYPES = new Set([
  'member',
  'chat-member',
  'group-member',
  'space-member',
  'user',
  'profile'
])
const ASSISTANT_MENTION_TYPES = new Set(['assistant', 'agent'])
const CRM_MENTION_TYPES = new Set(['lead', 'contact', 'company'])

function normalizeMentionPart(value: string | null | undefined) {
  const normalized = value?.replace(/\s+/g, ' ').trim()
  return normalized || null
}

function normalizeMentionType(value: string) {
  return value.trim().toLowerCase().replace(/[_\s]+/g, '-')
}

function isStructuredPlatformLosiMention(type: string, id: string) {
  if (type === 'assistant' || type === 'agent') {
    return false
  }

  const normalizedId = id.trim().toLowerCase()

  return (
    type === 'losi' ||
    type === 'platform-losi' ||
    (type === 'platform' &&
      (normalizedId === 'losi' ||
        normalizedId === 'losi-assistant' ||
        normalizedId === 'losi-assistant-mention'))
  )
}

function getStructuredMentionKind(
  type: string,
  id: string
): ChatStructuredMentionKind {
  if (isStructuredPlatformLosiMention(type, id)) {
    return 'platform_losi'
  }

  if (type === 'everyone') {
    return 'everyone'
  }

  if (type === 'space-member') {
    return 'space_member'
  }

  if (MEMBER_MENTION_TYPES.has(type)) {
    return 'member'
  }

  if (ASSISTANT_MENTION_TYPES.has(type)) {
    return 'assistant'
  }

  if (CRM_MENTION_TYPES.has(type)) {
    return 'crm'
  }

  return 'unknown'
}

function summarizeStructuredMentions(
  items: ChatStructuredMention[],
  messageText: string
): ChatStructuredMentions {
  const memberUserIds = new Set<string>()
  const assistantIds = new Set<string>()

  for (const item of items) {
    if (item.kind === 'member' || item.kind === 'space_member') {
      memberUserIds.add(item.id)
    }

    if (item.kind === 'assistant') {
      assistantIds.add(item.id)
    }
  }

  return {
    items,
    memberUserIds: [...memberUserIds],
    assistantIds: [...assistantIds],
    everyoneMentioned: items.some((item) => item.kind === 'everyone'),
    platformLosiMentioned:
      isGroupChatAssistantMentioned(messageText) ||
      items.some((item) => item.kind === 'platform_losi')
  }
}

function parseStructuredMentions(messageText: string): ChatStructuredMentions {
  const items: ChatStructuredMention[] = []
  const seen = new Set<string>()
  STRUCTURED_MENTION_PATTERN.lastIndex = 0

  let match: RegExpExecArray | null
  while ((match = STRUCTURED_MENTION_PATTERN.exec(messageText)) !== null) {
    const label = normalizeMentionPart(match[1])
    const type = normalizeMentionType(match[2] ?? '')
    const id = normalizeMentionPart(match[3])

    if (!label || !type || !id) {
      continue
    }

    const key = `${type}:${id}`
    if (seen.has(key)) {
      continue
    }

    seen.add(key)
    items.push({
      kind: getStructuredMentionKind(type, id),
      type,
      id,
      label: label.replace(/^@+/, '')
    })
  }

  return summarizeStructuredMentions(items, messageText)
}

function mergeStructuredMentions(
  parsedMentions: ChatStructuredMentions,
  optionMentions?: ChatStructuredMentions
): ChatStructuredMentions {
  if (!optionMentions) {
    return parsedMentions
  }

  const items: ChatStructuredMention[] = []
  const seen = new Set<string>()

  for (const item of [...parsedMentions.items, ...optionMentions.items]) {
    const key = `${item.type}:${item.id}`
    if (seen.has(key)) {
      continue
    }

    seen.add(key)
    items.push(item)
  }

  return {
    ...summarizeStructuredMentions(items, ''),
    platformLosiMentioned:
      parsedMentions.platformLosiMentioned || optionMentions.platformLosiMentioned
  }
}

function getMentionedMemberUserIds(
  mentions: ChatStructuredMentions,
  currentUserId?: string | null
) {
  const normalizedCurrentUserId = currentUserId?.trim()

  return mentions.memberUserIds.filter(
    (userId) => userId && userId !== normalizedCurrentUserId
  )
}

function getMentionedMemberLabels(mentions: ChatStructuredMentions) {
  return mentions.items
    .filter((item) => item.kind === 'member' || item.kind === 'space_member')
    .map((item) => item.label.trim())
    .filter(Boolean)
}

function getMentionStartedGroupChatName(mentions: ChatStructuredMentions) {
  const labels = getMentionedMemberLabels(mentions)

  if (labels.length === 0) {
    return null
  }

  const visibleLabels = labels.slice(0, 3)
  const remainingCount = labels.length - visibleLabels.length

  return remainingCount > 0
    ? `${visibleLabels.join(', ')} + ${remainingCount}`
    : visibleLabels.join(', ')
}

function getStructuredMentionAnnotation(
  mentions: ChatStructuredMentions
): Json | null {
  if (mentions.items.length === 0) {
    return null
  }

  return {
    items: mentions.items,
    memberUserIds: mentions.memberUserIds,
    assistantIds: mentions.assistantIds,
    everyoneMentioned: mentions.everyoneMentioned,
    platformLosiMentioned: mentions.platformLosiMentioned
  } as Json
}

async function readGroupChatCreateError(response: Response) {
  const payload = (await response.json().catch(() => null)) as {
    error?: string
  } | null

  return payload?.error || 'Failed to create group chat'
}

async function createMentionOnlyGroupChatFromSource(
  sourceChatId: string,
  memberUserIds: string[],
  name: string | null
) {
  const response = await authenticatedFetch('/api/chat/groups', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      sourceChatId,
      name,
      responseMode: 'mention_only',
      memberIds: memberUserIds
    })
  })

  if (!response.ok) {
    throw new Error(await readGroupChatCreateError(response))
  }

  const payload = (await response.json().catch(() => null)) as {
    chat?: Tables<'chats'>
  } | null

  if (!payload?.chat) {
    throw new Error('Failed to create group chat')
  }

  return normalizeChatForClient(payload.chat)
}

function dispatchGroupMentionNotifications(chatId: string, messageId: string) {
  void authenticatedFetch(`/api/chat/groups/${encodeURIComponent(chatId)}/mentions`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ messageId })
  })
    .then(async (response) => {
      if (response.ok) {
        return
      }

      const details = await response.text().catch(() => '')
      safeConsole.warn('Failed to notify group mentions:', {
        status: response.status,
        details
      })
    })
    .catch((error) => {
      safeConsole.warn('Failed to notify group mentions:', error)
    })
}

export const useChatHandler = ({
  onChatCreate,
  onChatUpdate,
  chatId: explicitChatId,
  forceDraftChat = false,
  enableRealtimeFallback = false,
  canFocusInput = true,
  adjacentPaneContext = null
}: UseChatHandlerProps = {}) => {
  const isAbortLikeError = useCallback((error: unknown) => {
    if (!(error instanceof Error)) {
      return false
    }

    const normalizedMessage = error.message.toLowerCase()

    return (
      error.name === 'AbortError' ||
      normalizedMessage.includes('aborted') ||
      normalizedMessage.includes('abort')
    )
  }, [])

  const router = useRouter()
  const pathname = usePathname()

  const {
    chats = [],
    chatFiles,
    profile,
    selectedWorkspace,
    setChats,
    allModels,
    newMessageImages,
    selectedAssistant,
    chatImages,
    setChatFiles,
    newMessageFiles,
    activeAttachmentKey,
    getAttachmentStoreKey,
    getScopedAttachmentBindings,
    migrateAttachmentState,
    setToolInUse,
    useRetrieval,
    sourceCount,
    setIsPromptPickerOpen,
    setIsFilePickerOpen,
    selectedPreset,
    models,
    setFiles,
    setIsPaywallOpen,
    isThinkDeeperEnabled,
    tools
  } = useContext(ChatbotUIContext)

  const {
    setChatMessages,
    selectedChat,
    setSelectedChat,
    setSelectedTools,
    chatSettings,
    chatMessages,
    chatFileItems,
    selectedTools,
    getSelectedToolsSnapshot,
    setChatSettings,
    selectedHtmlElements,
    setSelectedHtmlElements,
    setIsNewChat,
    activeChatKey,
    getChatStoreKey,
    getScopedChatBindings,
    migrateChatState,
    pendingNavigationChatId,
    setPendingNavigationChatId,
    setActiveChatOverrideId
  } = useContext(ChatbotUIChatContext)

  const setUserInput = useScopedSetUserInput()
  const [, addChat] = useAtom(addChatAtom)
  const [, updateChatAtomValue] = useAtom(updateChatAtom)
  const [realtimeActiveChatId, setRealtimeActiveChatId] = useAtom(
    realtimeActiveChatIdAtom
  )
  // Create-once guard — double-clicks / onConnected must not mint a second chat.
  const realtimeChatCreateInFlightRef = useRef(false)
  // Stable id for transcript persist while /chat draft briefly clears selectedChat.
  const realtimeBoundChatIdRef = useRef<string | null>(null)
  const { addEventListener, removeEventListener } = useServerEvents()
  const scopedDraftAttachmentKey = useScopedAttachmentKey({
    activeAttachmentKey,
    chatId: null,
    getAttachmentStoreKey
  })

  const chatInputRef = useRef<HTMLTextAreaElement>(null)
  const paywallTimerRef = useRef<ReturnType<typeof window.setTimeout> | null>(
    null
  )
  const pendingChatRequestRef = useRef<PendingChatRequestState | null>(null)
  /** When true, abort/stop must never bounce a draft back into the composer
   * (force-send / explicit stop-without-restore). Cleared on accept. */
  const suppressDraftRestoreRef = useRef(false)
  const activeRequestChatStoreKeyRef = useRef<string | null>(null)
  const activeRequestStartedAtRef = useRef<number | null>(null)
  const pendingTitleGenerationChatIdsRef = useRef<Set<string>>(new Set())
  const generatedOpeningChatTitleByIdRef = useRef<Map<string, string>>(new Map())
  const normalizedExplicitChatId =
    typeof explicitChatId === 'string' && explicitChatId.trim()
      ? explicitChatId.trim()
      : null
  const explicitChatStoreKey = normalizedExplicitChatId
    ? getChatStoreKey(normalizedExplicitChatId)
    : null
  const explicitAttachmentKey = normalizedExplicitChatId
    ? getAttachmentStoreKey(normalizedExplicitChatId)
    : null
  const scopedExplicitChatBindings =
    explicitChatStoreKey ? getScopedChatBindings(explicitChatStoreKey) : null
  const scopedExplicitChatState = scopedExplicitChatBindings?.getSnapshot() ?? null
  const scopedExplicitAttachmentBindings =
    explicitAttachmentKey
      ? getScopedAttachmentBindings(explicitAttachmentKey)
      : null
  const scopedExplicitAttachmentState =
    scopedExplicitAttachmentBindings?.getSnapshot() ?? null
  const visibleChatMessages =
    scopedExplicitChatState?.chatMessages ?? chatMessages
  const visibleChatFileItems =
    scopedExplicitChatState?.chatFileItems ?? chatFileItems
  const visibleChatFiles =
    scopedExplicitAttachmentState?.chatFiles ?? chatFiles
  const visibleChatImages =
    scopedExplicitAttachmentState?.chatImages ?? chatImages
  const visibleNewMessageFiles =
    scopedExplicitAttachmentState?.newMessageFiles ?? newMessageFiles
  const visibleNewMessageImages =
    scopedExplicitAttachmentState?.newMessageImages ?? newMessageImages
  const setVisibleChatMessages =
    scopedExplicitChatBindings?.setChatMessages ?? setChatMessages
  const setVisibleChatFiles =
    scopedExplicitAttachmentBindings?.setChatFiles ?? setChatFiles

  const prepareDesktopHandoffFiles = useCallback(
    async (
      attachments: DesktopHandoffAttachment[],
      effectiveProfile: Tables<'profiles'>,
      workspaceId: string,
      embeddingsProvider: 'openai' | 'local'
    ): Promise<ChatFile[]> => {
      const preparedFiles = await Promise.all(
        attachments.map(async (attachment) => {
          if (attachment.bucket === 'files' && attachment.path) {
            const createdFile = await createFileFromStoragePath(
              {
                user_id: effectiveProfile.user_id,
                description: '',
                file_path: attachment.path,
                name: attachment.name || `attachment-${Date.now()}`,
                size: attachment.size,
                tokens: 0,
                type: inferDesktopHandoffFileType(attachment)
              },
              workspaceId,
              embeddingsProvider,
              attachment.path
            )

            setFiles((previousFiles) =>
              previousFiles.some((existingFile) => existingFile.id === createdFile.id)
                ? previousFiles
                : [...previousFiles, createdFile]
            )

            return {
              id: createdFile.id,
              name: createdFile.name,
              type: createdFile.type,
              file: null
            }
          }

          if (!attachment.signedUrl) {
            throw new Error(`${attachment.name || 'Attachment'} is unavailable.`)
          }

          const response = await fetch(attachment.signedUrl)
          if (!response.ok) {
            throw new Error(`Failed to load ${attachment.name || 'attachment'}.`)
          }

          const blob = await response.blob()
          const file = new File([blob], attachment.name || `attachment-${Date.now()}`, {
            type:
              attachment.mimeType || attachment.type || blob.type || 'application/octet-stream'
          })

          const createdFile = await createFile(
            file,
            {
              user_id: effectiveProfile.user_id,
              description: '',
              file_path: '',
              name: file.name,
              size: file.size || attachment.size,
              tokens: 0,
              type: inferDesktopHandoffFileType(attachment, file)
            },
            workspaceId,
            embeddingsProvider
          )

          setFiles((previousFiles) =>
            previousFiles.some((existingFile) => existingFile.id === createdFile.id)
              ? previousFiles
              : [...previousFiles, createdFile]
          )

          return {
            id: createdFile.id,
            name: createdFile.name,
            type: createdFile.type,
            file
          }
        })
      )

      return mergeChatFiles([], preparedFiles)
    },
    [setFiles]
  )

  useEffect(() => {
    return () => {
      if (paywallTimerRef.current) {
        clearTimeout(paywallTimerRef.current)
      }
    }
  }, [])

  const scheduleUpgradePaywall = useCallback(() => {
    if (!setIsPaywallOpen) {
      return
    }

    if (paywallTimerRef.current) {
      clearTimeout(paywallTimerRef.current)
    }

    paywallTimerRef.current = scheduleDelayedPaywall(() => setIsPaywallOpen(true))
  }, [setIsPaywallOpen])

  const scheduleFirstResponseTitleGeneration = useCallback(
    ({
      chatId,
      userMessage,
      assistantMessage
    }: {
      chatId: string
      userMessage: string
      assistantMessage: string
    }) => {
      if (!chatId.trim()) return

      if (pendingTitleGenerationChatIdsRef.current.has(chatId)) {
        return
      }

      pendingTitleGenerationChatIdsRef.current.add(chatId)

      void (async () => {
        try {
          const defaultChatTitle = t('chat_helper.title_generator.default_title')
          const normalizedUserMessage = userMessage.trim()

          if (!normalizedUserMessage) {
            return
          }

          const assistantWithoutSuggestions =
            stripAssistantResponseControlMarkers(assistantMessage).trim()

          const generatedName = await generateChatName(
            normalizedUserMessage.slice(0, 100),
            assistantWithoutSuggestions.slice(0, 1200)
          )
          const cleanedGeneratedName = generatedName.replace(/"/g, '').trim()

          if (
            !cleanedGeneratedName ||
            cleanedGeneratedName.toLowerCase() === defaultChatTitle.toLowerCase()
          ) {
            return
          }

          const updatedChat = await updateChatInDB(chatId, {
            name: cleanedGeneratedName
          })
          const localChat =
            (selectedChat?.id === chatId ? selectedChat : null) ??
            chats.find((chat) => chat.id === chatId)
          const normalizedUpdatedChat = {
            ...updatedChat,
            updated_at: localChat?.updated_at ?? updatedChat.updated_at,
            metadata: updatedChat.metadata ?? null,
            type: updatedChat.type ?? null
          }
          generatedOpeningChatTitleByIdRef.current.set(
            chatId,
            normalizedUpdatedChat.name
          )

          setChats((previousChats) =>
            previousChats.map((chat) =>
              chat.id === chatId
                ? { ...normalizedUpdatedChat, updated_at: chat.updated_at }
                : chat
            )
          )
          updateChatAtomValue(normalizedUpdatedChat)
          setSelectedChat((previousChat) =>
            previousChat?.id === chatId
              ? { ...normalizedUpdatedChat, updated_at: previousChat.updated_at }
              : previousChat
          )

          if (onChatUpdate) {
            onChatUpdate(normalizedUpdatedChat)
          }
        } catch (error) {
          safeConsole.warn('Failed to generate chat title after first response', error)
        } finally {
          pendingTitleGenerationChatIdsRef.current.delete(chatId)
        }
      })()
    },
    [chats, onChatUpdate, selectedChat, setChats, setSelectedChat, updateChatAtomValue]
  )

  const clearStoppedPendingTurn = useCallback(
    (bindings: ScopedChatBindings, options?: ChatStopOptions) => {
      const snapshot = bindings.getSnapshot()
      const messagesWithoutStale = stopStalePendingAssistantTurns(
        snapshot.chatMessages
      )
      const { nextMessages, restoredUserInput, removedPendingAssistantTurn } =
        getPendingAssistantTurnCleanup(messagesWithoutStale)
      const shouldRestoreInput = options?.restoreInput !== false

      if (removedPendingAssistantTurn) {
        bindings.setChatMessages(nextMessages)
      } else if (messagesWithoutStale !== snapshot.chatMessages) {
        bindings.setChatMessages(messagesWithoutStale)
        const pendingTurn = getLatestPendingAssistantTurnState(
          messagesWithoutStale
        )

        if (pendingTurn) {
          bindings.setChatMessages((previousMessages) =>
            previousMessages.map((chatMessage) => {
              if (chatMessage.message.id !== pendingTurn.message.message.id) {
                return chatMessage
              }

              return {
                ...chatMessage,
                message: {
                  ...chatMessage.message,
                  metadata: stopPendingAssistantTurnMetadata(
                    chatMessage.message.metadata
                  ) as ChatMessage['message']['metadata']
                }
              }
            })
          )
        }
      } else {
        const pendingTurn = getLatestPendingAssistantTurnState(
          snapshot.chatMessages
        )

        if (pendingTurn) {
          bindings.setChatMessages((previousMessages) =>
            previousMessages.map((chatMessage) => {
              if (chatMessage.message.id !== pendingTurn.message.message.id) {
                return chatMessage
              }

              return {
                ...chatMessage,
                message: {
                  ...chatMessage.message,
                  metadata: stopPendingAssistantTurnMetadata(
                    chatMessage.message.metadata
                  ) as ChatMessage['message']['metadata']
                }
              }
            })
          )
        }
      }

      const fallbackInput =
        shouldRestoreInput && removedPendingAssistantTurn
          ? restoredUserInput ??
            (pendingChatRequestRef.current &&
            !pendingChatRequestRef.current.hasOutput
              ? pendingChatRequestRef.current.input
              : null)
          : null

      if (fallbackInput !== null) {
        const stillHasUserTurn = nextMessages.some(
          (chatMessage) =>
            chatMessage.message.role === 'user' &&
            typeof chatMessage.message.content === 'string' &&
            chatMessage.message.content === fallbackInput
        )
        if (!stillHasUserTurn) {
          setUserInput(fallbackInput)
        }
      }

      bindings.setIsGenerating(false)
      bindings.setIsPersisting(false)
      bindings.setFirstTokenReceived(false)
      bindings.setAbortController(null)
      setToolInUse('none')
      pendingChatRequestRef.current = null
      activeRequestChatStoreKeyRef.current = null
      activeRequestStartedAtRef.current = null
    },
    [setToolInUse, setUserInput]
  )
  const persistStoppedPendingTurn = useCallback((bindings: ScopedChatBindings) => {
    const pendingTurn = getLatestPendingAssistantTurnState(
      bindings.getSnapshot().chatMessages
    )

    if (!pendingTurn) {
      return
    }

    const pendingMessage = pendingTurn.message.message
    const metadata = stopPendingAssistantTurnMetadata(pendingMessage.metadata)
    // Force-send / Stop must flush live tool cards (image/video timeline) too —
    // metadata-only writes left empty completed rows after reload.
    void updateMessage(pendingMessage.id, {
      metadata,
      content:
        typeof pendingMessage.content === 'string' ? pendingMessage.content : '',
      annotation: (pendingMessage.annotation as Json | null | undefined) ?? null
    }).catch((error) => {
      safeConsole.warn('Failed to persist stopped pending assistant turn', error)
    })
  }, [])
  const isCurrentRealtimeToolEvent = useCallback((timestamp?: number) => {
    const requestStartedAt = activeRequestStartedAtRef.current

    if (!activeRequestChatStoreKeyRef.current || !requestStartedAt) {
      return false
    }

    if (typeof timestamp !== 'number' || !Number.isFinite(timestamp)) {
      return true
    }

    return timestamp >= requestStartedAt - 500
  }, [])
  const getBindingsForEventChatId = useCallback(
    (chatId?: string | null) => {
      const trimmedChatId = typeof chatId === 'string' ? chatId.trim() : ''
      if (!trimmedChatId) {
        return null
      }

      return getScopedChatBindings(getChatStoreKey(trimmedChatId))
    },
    [getChatStoreKey, getScopedChatBindings]
  )
  const getBindingsForRealtimeToolEvent = useCallback(
    (chatId?: string | null, timestamp?: number) => {
      const explicitBindings = getBindingsForEventChatId(chatId)

      if (explicitBindings && !isCurrentRealtimeToolEvent(timestamp)) {
        const pendingTurn = getLatestPendingAssistantTurnState(
          explicitBindings.getSnapshot().chatMessages
        )
        const eventStartedAfterPendingTurn =
          pendingTurn &&
          (
            typeof timestamp !== 'number' ||
            !Number.isFinite(timestamp) ||
            timestamp >= pendingTurn.startedAt - 500
          )

        return eventStartedAfterPendingTurn ? explicitBindings : null
      }

      if (explicitBindings) {
        return explicitBindings
      }

      if (!isCurrentRealtimeToolEvent(timestamp)) {
        return null
      }

      const requestChatStoreKey = activeRequestChatStoreKeyRef.current
      return requestChatStoreKey
        ? getScopedChatBindings(requestChatStoreKey)
        : null
    },
    [getBindingsForEventChatId, getScopedChatBindings, isCurrentRealtimeToolEvent]
  )

  useEffect(() => {
    if (!enableRealtimeFallback) return

    const handleStreamAnnotation = (event: {
      timestamp?: number
      data?: {
        annotation?: Record<string, unknown>
        chatId?: string | null
      }
    }) => {
      const annotation = event.data?.annotation
      if (!annotation || typeof annotation !== 'object') return

      const targetBindings = getBindingsForRealtimeToolEvent(
        event.data?.chatId,
        event.timestamp
      )
      if (!targetBindings) return

      targetBindings.setChatMessages((prev) => {
        const targetIdx = findAssistantMessageIndexForToolEvent(prev)
        if (targetIdx === -1) return prev

        return prev.map((message, index) => {
          if (index !== targetIdx) return message

          return {
            ...message,
            message: {
              ...message.message,
              annotation: mergeAssistantMessageAnnotation(
                (message.message.annotation as Json | null | undefined) ?? null,
                annotation as Json
              )
            }
          }
        })
      })
    }

    addEventListener('stream.annotation', handleStreamAnnotation)

    return () => {
      removeEventListener('stream.annotation', handleStreamAnnotation)
    }
  }, [
    addEventListener,
    enableRealtimeFallback,
    getBindingsForRealtimeToolEvent,
    removeEventListener
  ])

  useEffect(() => {
    if (!enableRealtimeFallback) return

    const isAutoExpandedTool = (toolName: string) =>
      toolName.includes('googleSearch') ||
      toolName.includes('webScraper__googleSearch') ||
      toolName.includes('unifiedImage') ||
      toolName.includes('imageGeneration') ||
      toolName.includes('imageEdit') ||
      toolName.includes('videoGeneration') ||
      toolName.includes('videoGenerator') ||
      toolName.includes('getWeather') ||
      toolName.includes('webScraper__getWeather') ||
      toolName.startsWith('ContentEngine__')

    const updateTimeline = (
      updater: (timeline: ToolTimelineEntry[]) => ToolTimelineEntry[],
      target?: {
        toolId?: string
        toolName?: string
        chatId?: string | null
        timestamp?: number
        persist?: boolean
      }
    ) => {
      const targetBindings = getBindingsForRealtimeToolEvent(
        target?.chatId,
        target?.timestamp
      )
      if (!targetBindings) return

      let annotationToPersist: { messageId: string; annotation: Json } | null = null

      targetBindings.setChatMessages((prev) => {
        const targetIdx = findAssistantMessageIndexForToolEvent(
          prev,
          target?.toolId,
          target?.toolName
        )
        if (targetIdx === -1) return prev

        return prev.map((message, index) => {
          if (index !== targetIdx) return message

          const annotation =
            message.message.annotation && typeof message.message.annotation === 'object'
              ? (message.message.annotation as Record<string, unknown>)
              : {}
          const rawTimeline = annotation.tool_timeline
          const timeline = Array.isArray(rawTimeline)
            ? (rawTimeline as ToolTimelineEntry[])
            : []
          const nextTimeline = updater([...timeline]).sort((left, right) => {
            const leftSequence =
              typeof left.sequence === 'number' ? left.sequence : Number.MAX_SAFE_INTEGER
            const rightSequence =
              typeof right.sequence === 'number' ? right.sequence : Number.MAX_SAFE_INTEGER

            if (leftSequence !== rightSequence) {
              return leftSequence - rightSequence
            }

            return left.startedAt - right.startedAt
          })

          const nextAnnotation = {
            ...annotation,
            tool_timeline: nextTimeline
          } as unknown as Json
          const messageId = message.message.id
          // Persist terminal tool results immediately so reload / force-send
          // cannot drop render_markdown media that never made it into content.
          if (
            target?.persist &&
            typeof messageId === 'string' &&
            messageId.length > 0 &&
            !messageId.startsWith('temp') &&
            !messageId.startsWith('realtime-')
          ) {
            annotationToPersist = {
              messageId,
              annotation: nextAnnotation
            }
          }

          return {
            ...message,
            message: {
              ...message.message,
              annotation: nextAnnotation
            }
          }
        })
      })

      if (annotationToPersist) {
        void updateMessage(annotationToPersist.messageId, {
          annotation: annotationToPersist.annotation
        }).catch((error) => {
          safeConsole.warn('Failed to persist live tool timeline annotation', error)
        })
      }
    }

    const findEntryIndex = (
      timeline: ToolTimelineEntry[],
      toolId?: string,
      toolName?: string
    ) => {
      if (toolId === 'sandbox' && toolName === 'sandbox__runAgentBrowser') {
        const browserIndex = timeline.findLastIndex((entry) => entry.name === toolName)
        if (browserIndex >= 0) return browserIndex
      }

      if (toolId) {
        return timeline.findIndex((entry) => entry.id === toolId)
      }

      if (toolName) {
        return timeline.findLastIndex((entry) => entry.name === toolName)
      }

      return -1
    }

    const getNextSequence = (timeline: ToolTimelineEntry[]) => {
      let maxSequence = -1

      for (const entry of timeline) {
        if (typeof entry.sequence === 'number' && entry.sequence > maxSequence) {
          maxSequence = entry.sequence
        }
      }

      return maxSequence + 1
    }

    const handleToolStart = (event: {
      timestamp?: number
      data?: {
        toolId?: string
        toolName?: string
        request?: unknown
        sequence?: number
        chatId?: string | null
      }
    }) => {
      const toolId = event.data?.toolId
      const toolName = event.data?.toolName
      if (!toolId && !toolName) return

      const name = toolName ?? toolId ?? 'Tool'
      const id =
        toolId === 'sandbox' && toolName === 'sandbox__runAgentBrowser'
          ? name
          : toolId ?? toolName ?? 'tool'

      updateTimeline((timeline) => {
        const index = findEntryIndex(timeline, id, name)
        const now = Date.now()
        const sequence =
          typeof event.data?.sequence === 'number'
            ? event.data.sequence
            : index >= 0 && typeof timeline[index].sequence === 'number'
              ? timeline[index].sequence
              : getNextSequence(timeline)
        const nextEntry: ToolTimelineEntry = {
          id,
          name,
          status: 'running',
          startedAt: index >= 0 ? timeline[index].startedAt : now,
          request: event.data?.request,
          sequence,
          isExpanded: isAutoExpandedTool(name)
        }

        if (index >= 0) {
          const nextTimeline = [...timeline]
          nextTimeline[index] = { ...nextTimeline[index], ...nextEntry }
          return nextTimeline
        }

        return [...timeline, nextEntry]
      }, { toolId, toolName, chatId: event.data?.chatId, timestamp: event.timestamp })
    }

    const handleToolProgress = (event: {
      timestamp?: number
      data?: {
        toolId?: string
        toolName?: string
        chatId?: string | null
        progress?: { message?: string; percent?: number; step?: string }
        message?: string
        percent?: number
        step?: string
      }
    }) => {
      const toolId = event.data?.toolId
      const toolName = event.data?.toolName
      if (!toolId && !toolName) return

      const name = toolName ?? toolId ?? 'Tool'
      const id =
        toolId === 'sandbox' && toolName === 'sandbox__runAgentBrowser'
          ? name
          : toolId ?? toolName ?? 'tool'

      updateTimeline((timeline) => {
        const index = findEntryIndex(timeline, toolId, toolName)
        const now = Date.now()
        const progress = event.data?.progress ?? {
          message: event.data?.message,
          percent: event.data?.percent,
          step: event.data?.step
        }

        if (index >= 0) {
          const nextTimeline = [...timeline]
          const currentEntry = nextTimeline[index]
          nextTimeline[index] = {
            ...currentEntry,
            status:
              currentEntry.status === 'complete' || currentEntry.status === 'error'
                ? currentEntry.status
                : 'progress',
            progress: {
              ...currentEntry.progress,
              ...progress
            },
            isExpanded: currentEntry.isExpanded ?? isAutoExpandedTool(name)
          }
          return nextTimeline
        }

        return [
          ...timeline,
          {
            id,
            name,
            status: 'progress',
            startedAt: now,
            sequence: getNextSequence(timeline),
            progress,
            isExpanded: isAutoExpandedTool(name)
          }
        ]
      }, { toolId, toolName, chatId: event.data?.chatId, timestamp: event.timestamp })
    }

    const handleToolComplete = (event: {
      timestamp?: number
      data?: {
        toolId?: string
        toolName?: string
        result?: unknown
        sequence?: number
        chatId?: string | null
      }
    }) => {
      const toolId = event.data?.toolId
      const toolName = event.data?.toolName
      if (!toolId && !toolName) return

      const name = toolName ?? toolId ?? 'Tool'
      const id =
        toolId === 'sandbox' && toolName === 'sandbox__runAgentBrowser'
          ? name
          : toolId ?? toolName ?? 'tool'

      updateTimeline((timeline) => {
        const index = findEntryIndex(timeline, toolId, toolName)
        const now = Date.now()
        const sequence =
          typeof event.data?.sequence === 'number'
            ? event.data.sequence
            : index >= 0 && typeof timeline[index].sequence === 'number'
              ? timeline[index].sequence
              : getNextSequence(timeline)
        const nextEntry: ToolTimelineEntry = {
          id,
          name,
          status: 'complete',
          startedAt: index >= 0 ? timeline[index].startedAt : now,
          endedAt: now,
          result: event.data?.result,
          sequence,
          isExpanded: isAutoExpandedTool(name)
        }

        if (index >= 0) {
          const nextTimeline = [...timeline]
          const currentEntry = nextTimeline[index]
          nextTimeline[index] = {
            ...currentEntry,
            ...nextEntry,
            startedAt: currentEntry.startedAt,
            sequence: currentEntry.sequence,
            endedAt: nextEntry.endedAt
          }
          return nextTimeline
        }

        return [...timeline, nextEntry]
      }, {
        toolId,
        toolName,
        chatId: event.data?.chatId,
        timestamp: event.timestamp,
        persist: true
      })
    }

    const handleToolError = (event: {
      timestamp?: number
      data?: {
        toolId?: string
        toolName?: string
        error?: string
        sequence?: number
        chatId?: string | null
      }
    }) => {
      const toolId = event.data?.toolId
      const toolName = event.data?.toolName
      if (!toolId && !toolName) return

      const name = toolName ?? toolId ?? 'Tool'
      const id =
        toolId === 'sandbox' && toolName === 'sandbox__runAgentBrowser'
          ? name
          : toolId ?? toolName ?? 'tool'

      updateTimeline((timeline) => {
        const index = findEntryIndex(timeline, toolId, toolName)
        const now = Date.now()
        const sequence =
          typeof event.data?.sequence === 'number'
            ? event.data.sequence
            : index >= 0 && typeof timeline[index].sequence === 'number'
              ? timeline[index].sequence
              : getNextSequence(timeline)
        const errorMessage =
          typeof event.data?.error === 'string' && event.data.error.trim()
            ? event.data.error.trim()
            : 'Tool execution failed'
        const nextEntry: ToolTimelineEntry = {
          id,
          name,
          status: 'error',
          startedAt: index >= 0 ? timeline[index].startedAt : now,
          endedAt: now,
          error: errorMessage,
          // Always persist a structured result — opaque status=error with null
          // result breaks audits and prior-tool continuity.
          result: {
            success: false,
            error: errorMessage,
            code: 'TOOL_EXECUTION_ERROR',
            message: errorMessage
          },
          sequence,
          isExpanded: isAutoExpandedTool(name)
        }

        if (index >= 0) {
          const nextTimeline = [...timeline]
          const currentEntry = nextTimeline[index]
          nextTimeline[index] = {
            ...currentEntry,
            ...nextEntry,
            startedAt: currentEntry.startedAt,
            sequence: currentEntry.sequence,
            endedAt: nextEntry.endedAt
          }
          return nextTimeline
        }

        return [...timeline, nextEntry]
      }, {
        toolId,
        toolName,
        chatId: event.data?.chatId,
        timestamp: event.timestamp,
        persist: true
      })
    }

    addEventListener('tool.start', handleToolStart)
    addEventListener('tool.progress', handleToolProgress)
    addEventListener('tool.complete', handleToolComplete)
    addEventListener('tool.error', handleToolError)

    return () => {
      removeEventListener('tool.start', handleToolStart)
      removeEventListener('tool.progress', handleToolProgress)
      removeEventListener('tool.complete', handleToolComplete)
      removeEventListener('tool.error', handleToolError)
    }
  }, [
    addEventListener,
    enableRealtimeFallback,
    getBindingsForRealtimeToolEvent,
    removeEventListener
  ])

  // Realtime mode is atom-backed so /chat → /chat/[id] remounts keep the
  // stop controls. True when the active/pending/route chat owns the session.
  const advancedVoiceMode = Boolean(
    realtimeActiveChatId &&
      (selectedChat?.id === realtimeActiveChatId ||
        pendingNavigationChatId === realtimeActiveChatId ||
        (typeof pathname === 'string' &&
          pathname.includes(`/chat/${realtimeActiveChatId}`)) ||
        realtimeBoundChatIdRef.current === realtimeActiveChatId)
  )

  useEffect(() => {
    if (realtimeActiveChatId) {
      realtimeBoundChatIdRef.current = realtimeActiveChatId
    }
  }, [realtimeActiveChatId])

  const [responseTimeToFirstToken, setResponseTimeToFirstToken] = useState<number>(0)
  const [responseTimeTotal, setResponseTimeTotal] = useState<number>(0)
  const [responseTokensTotal, setResponseTokensTotal] = useState<number>(0)
  const [requestTokensTotal, setRequestTokensTotal] = useState<number>(0)

  // Confirm an already-minted LiveKit session without creating a chat.
  // onConnected must call this — never handleStartAdvancedVoiceMode (BUG1).
  const handleConfirmAdvancedVoiceMode = useCallback(
    (chatIdHint?: string | null) => {
      const id =
        (typeof chatIdHint === 'string' && chatIdHint.trim()) ||
        selectedChat?.id ||
        pendingNavigationChatId ||
        realtimeActiveChatId ||
        realtimeBoundChatIdRef.current
      if (!id) return
      realtimeBoundChatIdRef.current = id
      setRealtimeActiveChatId(id)
    },
    [
      pendingNavigationChatId,
      realtimeActiveChatId,
      selectedChat?.id,
      setRealtimeActiveChatId
    ]
  )

  const handleStartAdvancedVoiceMode = async () => {
    // Chat realtime is GA. Wave from empty /chat mints a new chat, then goes live.

    if (selectedChat?.id) {
      realtimeBoundChatIdRef.current = selectedChat.id
      setRealtimeActiveChatId(selectedChat.id)
      return
    }

    if (realtimeActiveChatId || realtimeChatCreateInFlightRef.current) {
      // Already creating / already live — do not mint a second chat.
      if (realtimeActiveChatId) {
        realtimeBoundChatIdRef.current = realtimeActiveChatId
      }
      return
    }

    if (!profile || !selectedWorkspace) {
      toast.error('Sign in and pick a workspace to talk to Losi.')
      return
    }

    if (!chatSettings) {
      toast.error("Couldn't start a live chat right now. Try again in a moment.")
      return
    }

    realtimeChatCreateInFlightRef.current = true
    try {
      const createdChat = normalizeChatForClient(
        await handleCreateChat(
          chatSettings,
          profile,
          selectedWorkspace,
          '',
          selectedAssistant,
          [],
          selectedTools || [],
          setSelectedChat,
          setChats,
          setVisibleChatFiles,
          setSelectedTools,
          addChat,
          {
            skipGeneratedName: true,
            fallbackName: t('chat_helper.title_generator.default_title')
          }
        )
      )

      if (!createdChat?.id) {
        toast.error("Couldn't start a live chat. Try again.")
        return
      }

      realtimeBoundChatIdRef.current = createdChat.id
      setSelectedChat(createdChat)
      updateChatAtomValue(createdChat)
      // Must land on /chat/[id] before RealtimeComposerBar starts the
      // session — on bare /chat, chatId is forced null and session init
      // fails without a chat record (misreported as missing agentId).
      setPendingNavigationChatId(createdChat.id)
      // Enable realtime BEFORE navigate so the destination remount still
      // sees the atom and mounts RealtimeComposerBar with stop controls.
      setRealtimeActiveChatId(createdChat.id)
      if (pathname === '/chat' || !pathname?.includes(createdChat.id)) {
        router.push(`/chat/${createdChat.id}`)
      }
    } catch {
      toast.error("Couldn't start a live chat. Try again.")
    } finally {
      realtimeChatCreateInFlightRef.current = false
    }
  }

  const handleEndAdvancedVoiceMode = () => {
    realtimeBoundChatIdRef.current = null
    setRealtimeActiveChatId(null)
  }

  const realtimePersistRef = useRef<{
    lastAssistantTempId: string | null
    lastAssistantDbId: string | null
    lastAssistantContent: string
    lastUserTempId: string | null
    lastUserDbId: string | null
    lastUserContent: string
    /** Preserved across assistant turn for first-response title gen. */
    titleUserContent: string
  }>({
    lastAssistantTempId: null,
    lastAssistantDbId: null,
    lastAssistantContent: '',
    lastUserTempId: null,
    lastUserDbId: null,
    lastUserContent: '',
    titleUserContent: ''
  })

  const onTranscriptUpdate = (
    transcript: string,
    role: 'user' | 'assistant' = 'assistant',
    meta?: { final?: boolean; id?: string }
  ) => {
    const text = transcript.trim()
    if (!text) return

    // Interim ASR: update the live bubble only — persist on final (BUG2).
    const isFinal = meta?.final !== false
    const segmentId =
      typeof meta?.id === 'string' && meta.id.trim() ? meta.id.trim() : null

    const chatId =
      selectedChat?.id ||
      realtimeActiveChatId ||
      realtimeBoundChatIdRef.current ||
      pendingNavigationChatId ||
      ''
    const userId =
      (profile as { user_id?: string } | null)?.user_id ||
      (selectedChat as { user_id?: string } | null)?.user_id ||
      ''

    let createdTempId: string | null = null
    let appendedContent: string | null = null
    let sequenceNumber = Date.now()
    let skippedPersist = false

    setVisibleChatMessages(prevMessages => {
      const updated = [...prevMessages]
      const last = updated[updated.length - 1]

      // Upsert by ASR segment id so interim chunks replace (stream live).
      if (segmentId) {
        const idx = updated.findIndex(entry => {
          const md = entry.message.metadata as
            | { realtime?: boolean; transcriptSegmentId?: string }
            | undefined
          return (
            entry.message.role === role &&
            Boolean(md?.realtime) &&
            md?.transcriptSegmentId === segmentId
          )
        })
        if (idx >= 0) {
          const entry = updated[idx]
          entry.message.content = text
          entry.message.word_count = text.split(/\s+/).filter(Boolean).length
          entry.message.updated_at = new Date().toISOString()
          appendedContent = text
          if (typeof entry.message.sequence_number === 'number') {
            sequenceNumber = entry.message.sequence_number
          }
          if (role === 'assistant') {
            realtimePersistRef.current.lastAssistantContent = text
            realtimePersistRef.current.lastAssistantTempId = entry.message.id
          } else {
            realtimePersistRef.current.lastUserContent = text
            realtimePersistRef.current.lastUserTempId = entry.message.id
            realtimePersistRef.current.titleUserContent = text
          }
          if (!isFinal) {
            skippedPersist = true
          } else {
            // First final for an interim-only bubble — persist via create path.
            const dbId =
              role === 'assistant'
                ? realtimePersistRef.current.lastAssistantDbId
                : realtimePersistRef.current.lastUserDbId
            if (!dbId && entry.message.id.startsWith('realtime-')) {
              createdTempId = entry.message.id
              appendedContent = null
            } else if (!dbId && entry.message.id) {
              // Bubble already has a persisted UUID but the dbId ref was
              // cleared (mode switch / remount). Update by message id.
              if (role === 'assistant') {
                realtimePersistRef.current.lastAssistantDbId = entry.message.id
              } else {
                realtimePersistRef.current.lastUserDbId = entry.message.id
              }
            }
          }
          return updated
        }
      }

      const lastIsRealtimeSameRole =
        last?.message?.role === role &&
        Boolean(
          last.message.metadata &&
            (last.message.metadata as { realtime?: boolean }).realtime
        )

      // Interim ASR: update the in-progress bubble. Finals are always their own
      // message (no newline-merge of same-side turns — that regression glued
      // separate utterances into one bubble).
      if (lastIsRealtimeSameRole && last && (isFinal || !segmentId)) {
        const lastMeta = last.message.metadata as
          | { realtime?: boolean; pending?: boolean; transcriptSegmentId?: string }
          | undefined
        const lastPending = Boolean(lastMeta?.pending)
        const lastHasSegment = Boolean(lastMeta?.transcriptSegmentId)

        if (!isFinal && segmentId) {
          // Fall through to create a new segment-tracked bubble.
        } else if (!isFinal && !segmentId && lastPending && !lastHasSegment) {
          // Replace last interim bubble content when no segment id.
          last.message.content = text
          last.message.word_count = text.split(/\s+/).filter(Boolean).length
          last.message.updated_at = new Date().toISOString()
          appendedContent = text
          if (role === 'assistant') {
            realtimePersistRef.current.lastAssistantContent = text
          } else {
            realtimePersistRef.current.lastUserContent = text
          }
          skippedPersist = true
          return updated
        } else if (isFinal && !segmentId && lastPending && !lastHasSegment) {
          // Finalize the pending interim (same utterance) — replace, don't append.
          last.message.content = text
          last.message.word_count = text.split(/\s+/).filter(Boolean).length
          last.message.updated_at = new Date().toISOString()
          last.message.metadata = {
            ...((last.message.metadata as object) || {}),
            realtime: true,
            pending: false,
            ...(segmentId ? { transcriptSegmentId: segmentId } : {})
          }
          appendedContent = text
          if (typeof last.message.sequence_number === 'number') {
            sequenceNumber = last.message.sequence_number
          }
          if (role === 'assistant') {
            realtimePersistRef.current.lastAssistantContent = text
            realtimePersistRef.current.lastAssistantTempId = last.message.id
          } else {
            realtimePersistRef.current.lastUserContent = text
            realtimePersistRef.current.titleUserContent = text
            realtimePersistRef.current.lastUserTempId = last.message.id
          }
          if (last.message.id.startsWith('realtime-')) {
            createdTempId = last.message.id
            appendedContent = null
          }
          return updated
        }
        // New final utterance (or interim with a finished prior bubble):
        // fall through and create a separate message.
      }

      const lastSeq =
        typeof last?.message?.sequence_number === 'number'
          ? last.message.sequence_number
          : updated.length
      sequenceNumber = lastSeq + 1
      const tempId = `realtime-${role}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      createdTempId = tempId
      if (role === 'assistant') {
        realtimePersistRef.current.lastAssistantTempId = tempId
        realtimePersistRef.current.lastAssistantDbId = null
        realtimePersistRef.current.lastAssistantContent = text
        realtimePersistRef.current.lastUserTempId = null
        realtimePersistRef.current.lastUserDbId = null
        realtimePersistRef.current.lastUserContent = ''
      } else {
        realtimePersistRef.current.lastUserTempId = tempId
        realtimePersistRef.current.lastUserDbId = null
        realtimePersistRef.current.lastUserContent = text
        realtimePersistRef.current.titleUserContent = text
        realtimePersistRef.current.lastAssistantTempId = null
        realtimePersistRef.current.lastAssistantDbId = null
        realtimePersistRef.current.lastAssistantContent = ''
      }

      updated.push({
        message: {
          role,
          content: text,
          assistant_id: null,
          chat_id: chatId,
          created_at: new Date().toISOString(),
          id: tempId,
          image_paths: [],
          model: role === 'assistant' ? 'losi-inference' : '',
          sequence_number: sequenceNumber,
          updated_at: null,
          user_id: userId,
          annotation: role === 'assistant' ? { tool_timeline: [] } : undefined,
          word_count: text.split(/\s+/).filter(Boolean).length,
          metadata: {
            realtime: true,
            pending: true,
            ...(segmentId ? { transcriptSegmentId: segmentId } : {})
          },
          type: null
        },
        fileItems: []
      } as unknown as ChatMessage)

      if (!isFinal) skippedPersist = true
      return updated
    })

    if (!isFinal || skippedPersist) return
    if (!chatId || !userId) return

    if (appendedContent) {
      const dbId =
        role === 'assistant'
          ? realtimePersistRef.current.lastAssistantDbId
          : realtimePersistRef.current.lastUserDbId
      if (dbId) {
        if (role === 'assistant') {
          realtimePersistRef.current.lastAssistantContent = appendedContent
        } else {
          realtimePersistRef.current.lastUserContent = appendedContent
        }
        void updateMessage(dbId, {
          content: appendedContent,
          metadata: {
            realtime: true,
            ...(segmentId ? { transcriptSegmentId: segmentId } : {})
          }
        }).catch(() => {
          // Persistence is best-effort; UI already shows the bubble.
        })
      }
      return
    }

    if (createdTempId) {
      const tempId = createdTempId
      void createMessage({
        role,
        content: text,
        chat_id: chatId,
        user_id: userId,
        model: role === 'assistant' ? 'losi-inference' : '',
        image_paths: [],
        sequence_number: sequenceNumber,
        assistant_id: null,
        annotation: role === 'assistant' ? ({ tool_timeline: [] } as never) : null,
        metadata: {
          realtime: true,
          ...(segmentId ? { transcriptSegmentId: segmentId } : {})
        }
      } as never)
        .then(created => {
          if (!created?.id) return
          if (
            role === 'assistant' &&
            realtimePersistRef.current.lastAssistantTempId === tempId
          ) {
            realtimePersistRef.current.lastAssistantDbId = created.id
            const latest =
              realtimePersistRef.current.lastAssistantContent || text
            if (latest !== text) {
              void updateMessage(created.id, {
                content: latest,
                metadata: { realtime: true }
              }).catch(() => {})
            }
          }
          if (
            role === 'user' &&
            realtimePersistRef.current.lastUserTempId === tempId
          ) {
            realtimePersistRef.current.lastUserDbId = created.id
            const latest = realtimePersistRef.current.lastUserContent || text
            if (latest !== text) {
              void updateMessage(created.id, {
                content: latest,
                metadata: { realtime: true }
              }).catch(() => {})
            }
          }
          setVisibleChatMessages(prev =>
            prev.map(entry =>
              entry.message.id === tempId
                ? {
                    ...entry,
                    message: {
                      ...entry.message,
                      id: created.id,
                      metadata: {
                        ...((entry.message.metadata as object) || {}),
                        realtime: true,
                        pending: false
                      }
                    }
                  }
                : entry
            )
          )
          // Same first-response title path as text chat — realtime was skipping it.
          if (role === 'assistant' && chatId) {
            const userForTitle =
              realtimePersistRef.current.titleUserContent.trim()
            if (userForTitle) {
              scheduleFirstResponseTitleGeneration({
                chatId,
                userMessage: userForTitle,
                assistantMessage:
                  realtimePersistRef.current.lastAssistantContent || text
              })
            }
          }
        })
        .catch(() => {
          // Persistence is best-effort; UI already shows the bubble.
        })
    }
  }

  const onRealtimeInferenceEvent = (event: {
    type: string
    name?: unknown
    ok?: unknown
    error?: unknown
    at?: unknown
  }) => {
    const toolName = typeof event.name === 'string' ? event.name : ''
    if (!toolName) return
    if (event.type !== 'tool_start' && event.type !== 'tool_end') return

    const now =
      typeof event.at === 'number' && Number.isFinite(event.at)
        ? event.at
        : Date.now()
    const chatId = selectedChat?.id || ''
    const userId =
      (profile as { user_id?: string } | null)?.user_id ||
      (selectedChat as { user_id?: string } | null)?.user_id ||
      ''

    setVisibleChatMessages(prevMessages => {
      const updated = [...prevMessages]
      let last = updated[updated.length - 1]
      const lastIsRealtimeAssistant =
        last?.message?.role === 'assistant' &&
        Boolean(
          last.message.metadata &&
            (last.message.metadata as { realtime?: boolean }).realtime
        )

      if (!lastIsRealtimeAssistant) {
        const lastSeq =
          typeof last?.message?.sequence_number === 'number'
            ? last.message.sequence_number
            : updated.length
        const tempId = `realtime-assistant-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
        realtimePersistRef.current = {
          ...realtimePersistRef.current,
          lastAssistantTempId: tempId,
          lastAssistantDbId: null,
          lastAssistantContent: ''
        }
        updated.push({
          message: {
            role: 'assistant',
            content: '',
            assistant_id: null,
            chat_id: chatId,
            created_at: new Date().toISOString(),
            id: tempId,
            image_paths: [],
            model: 'losi-inference',
            sequence_number: lastSeq + 1,
            updated_at: null,
            user_id: userId,
            annotation: { tool_timeline: [] },
            word_count: 0,
            metadata: { realtime: true, pending: true },
            type: null
          },
          fileItems: []
        } as unknown as ChatMessage)
        last = updated[updated.length - 1]
      }

      if (!last) return prevMessages

      const annotation =
        last.message.annotation && typeof last.message.annotation === 'object'
          ? ({ ...(last.message.annotation as object) } as Record<string, unknown>)
          : {}
      const rawTimeline = annotation.tool_timeline
      const timeline = Array.isArray(rawTimeline)
        ? ([...rawTimeline] as ToolTimelineEntry[])
        : []

      if (event.type === 'tool_start') {
        const existing = timeline.findLastIndex(entry => entry.name === toolName)
        if (existing >= 0 && timeline[existing].status === 'running') {
          return updated
        }
        timeline.push({
          id: `realtime-${toolName}-${now}`,
          name: toolName,
          status: 'running',
          startedAt: now,
          sequence: timeline.length,
          isExpanded: true
        })
      } else {
        const idx = timeline.findLastIndex(entry => entry.name === toolName)
        const ok = event.ok !== false
        if (idx >= 0) {
          timeline[idx] = {
            ...timeline[idx],
            status: ok ? 'complete' : 'error',
            endedAt: now,
            error:
              typeof event.error === 'string' ? event.error : timeline[idx].error
          }
        } else {
          timeline.push({
            id: `realtime-${toolName}-${now}`,
            name: toolName,
            status: ok ? 'complete' : 'error',
            startedAt: now,
            endedAt: now,
            sequence: timeline.length,
            error: typeof event.error === 'string' ? event.error : undefined,
            isExpanded: true
          })
        }
      }

      last.message.annotation = {
        ...annotation,
        tool_timeline: timeline
      } as never

      const dbId =
        realtimePersistRef.current.lastAssistantDbId ||
        (last.message.id && !String(last.message.id).startsWith('realtime-')
          ? last.message.id
          : null)
      if (dbId) {
        void updateMessage(dbId, {
          annotation: last.message.annotation as never
        }).catch(() => {})
      }

      return updated
    })
  }

  // TODO: ADVANCE VOICE MODE
  // useEffect(() => {
  //   if (advancedVoiceMode) {
  //     const client = getRealtimeClient()
  //     if (client) {
  //       client.on(
  //         "conversation.updated",
  //         ({
  //           item,
  //           delta
  //         }: {
  //           item: { type: string; role: string }
  //           delta: { transcript?: string }
  //         }) => {
  //           if (item.type === "message" && item.role === "assistant") {
  //             setChatMessages(prevMessages => {
  //               const updatedMessages = [...prevMessages]
  //               const lastMessage = updatedMessages[updatedMessages.length - 1]
  //               if (lastMessage.message.role === "assistant") {
  //                 lastMessage.message.content += delta?.transcript || ""
  //               } else {
  //                 updatedMessages.push({
  //                   message: {
  //                     role: "assistant",
  //                     content: delta?.transcript || "",
  //                     assistant_id: null,
  //                     chat_id: "",
  //                     created_at: "",
  //                     id: "",
  //                     image_paths: [],
  //                     model: "",
  //                     sequence_number: 0,
  //                     updated_at: null,
  //                     user_id: "",
  //                     annotation: undefined,
  //                     word_count: 0,
  //                     metadata: null,
  //                     type: null
  //                   },
  //                   fileItems: []
  //                 } as unknown as ChatMessage)
  //               }
  //               return updatedMessages
  //             })
  //           }
  //         }
  //       )
  //     }
  //   }
  // }, [advancedVoiceMode])

  const handleNewChat = async (redirectTo = '', chatMessages: ChatMessage[] = []) => {
    try {
      if (!selectedWorkspace) return
      activeRequestChatStoreKeyRef.current = null
      // Tear down realtime before clearing the chat — otherwise the old
      // LiveKit room keeps speaking into the new empty composer.
      realtimeBoundChatIdRef.current = null
      setRealtimeActiveChatId(null)

      // Process memories if there are existing chat messages and we have a selected chat
      // if (selectedChat?.id && chatMessages.length > 3) {
      //   try {
      //     await processChatMemories(
      //       chatMessages,
      //       selectedChat.id,
      //       profile!.user_id
      //     )
      //   } catch (error) {
      //     console.error("Failed to process chat memories:", error)
      //   }
      // }

      // Core chat functionality continues regardless of memory processing
      setIsNewChat(true)
      setUserInput('')
      setPendingNavigationChatId(null)

      let newChatMessages = chatMessages
      let newPrompt =
        selectedWorkspace.default_prompt || 'You are a friendly, helpful AI assistant.'

      // Check for and apply previous chat context RIGHT HERE
      const storedContext = localStorage.getItem('previousChatContext')
      if (storedContext) {
        try {
          const context = JSON.parse(storedContext)
          const summary = context.summary

          if (summary) {
            const contextualPrompt = `Previous conversation summary:\n${summary}\n\nContinuing from this context, please respond helpfully and acknowledge the previous conversation when relevant.`
            newPrompt = newPrompt ? `${newPrompt}\n\n${contextualPrompt}` : contextualPrompt

            // Add a system message to indicate context transfer
            newChatMessages = [
              {
                message: {
                  id: uuidv4(),
                  content: `Context from previous chat "${context.chatName}" has been carried over.`,
                  role: 'assistant',
                  // Fill in other required fields for ChatMessage
                  chat_id: '',
                  created_at: new Date().toISOString(),
                  user_id: '',
                  model: chatSettings?.model || 'losi-omega',
                  assistant_id: null,
                  sequence_number: 0,
                  updated_at: null,
                  image_paths: [],
                  annotation: null,
                  metadata: { immovable: true }, // Makes message non-deletable/editable
                  type: null,
                  word_count: 0
                },
                fileItems: []
              }
            ]
          }
        } catch (error) {}
      }

      const resetChatStoreKey = explicitChatStoreKey ?? getChatStoreKey(null)
      const canonicalDraftAttachmentKey = getAttachmentStoreKey(null)
      const resetAttachmentKey =
        explicitAttachmentKey ??
        (scopedDraftAttachmentKey === activeAttachmentKey
          ? canonicalDraftAttachmentKey
          : scopedDraftAttachmentKey)
      const draftChatBindings = getScopedChatBindings(resetChatStoreKey)
      const clearAttachmentDraft = (
        attachmentKey: string,
        options: { clearChatAttachments?: boolean } = {}
      ) => {
        const bindings = getScopedAttachmentBindings(attachmentKey)

        if (options.clearChatAttachments) {
          bindings.setChatFiles([])
          bindings.setChatImages([])
        }

        bindings.setNewMessageFiles([])
        bindings.setNewMessageImages([])
        bindings.setShowFilesDisplay(false)
      }
      draftChatBindings.setChatMessages(newChatMessages)
      draftChatBindings.setChatFileItems([])
      draftChatBindings.setAbortController(null)
      draftChatBindings.setIsGenerating(false)
      draftChatBindings.setIsPersisting(false)
      draftChatBindings.setFirstTokenReceived(false)
      draftChatBindings.setRequestTokensTotal(0)
      draftChatBindings.setResponseTimeToFirstToken(0)
      draftChatBindings.setResponseTimeTotal(0)
      draftChatBindings.setResponseTokensTotal(0)
      setSelectedChat(null)

      clearAttachmentDraft(resetAttachmentKey, { clearChatAttachments: true })
      clearAttachmentDraft(canonicalDraftAttachmentKey, { clearChatAttachments: true })
      if (activeAttachmentKey !== resetAttachmentKey) {
        clearAttachmentDraft(activeAttachmentKey)
      }
      clearAllTryTempFileContents()
      setIsPromptPickerOpen(false)
      setIsFilePickerOpen(false)
      setSelectedHtmlElements([])

      setToolInUse('none')
      if (selectedAssistant) {
        setChatSettings((prevSettings) => ({
          ...prevSettings,
          // chatName: storedContext?.chatName || selectedChat?.name || 'Previous Conversation',
          model: selectedAssistant.model as LLMID,
          prompt: selectedAssistant.prompt,
          temperature: selectedAssistant.temperature,
          contextLength: selectedAssistant.context_length,
          includeProfileContext: selectedAssistant.include_profile_context,
          includeWorkspaceInstructions: selectedAssistant.include_workspace_instructions,
          embeddingsProvider: selectedAssistant.embeddings_provider as 'openai' | 'local'
          // reflectMode: prevSettings.reflectMode
        }))

        let allFiles = []

        const assistantFiles = (await getAssistantFilesByAssistantId(selectedAssistant.id)).files
        allFiles = [...assistantFiles]
        const assistantCollections = (
          await getAssistantCollectionsByAssistantId(selectedAssistant.id)
        ).collections
        for (const collection of assistantCollections) {
          const collectionFiles = (await getCollectionFilesByCollectionId(collection.id)).files
          allFiles = [...allFiles, ...collectionFiles]
        }
        const assistantTools = (await getAssistantToolsByAssistantId(selectedAssistant.id))
          .tools as Tables<'tools'>[]

        setSelectedTools(
          assistantTools.map((tool) => {
            const toolWithDefaults = {
              ...tool,
              parameters: (params: any) => tool.schema || {},
              can_be_used_in_applications: false
            } as Tables<'tools'> & {
              parameters(parameters: any): Json
              can_be_used_in_applications: boolean
            }
            return toolWithDefaults
          })
        )
        getScopedAttachmentBindings(resetAttachmentKey).setChatFiles(
          allFiles.map((file) => ({
            id: file.id,
            name: file.name,
            type: file.type,
            file: null
          }))
        )

        if (allFiles.length > 0) {
          getScopedAttachmentBindings(resetAttachmentKey).setShowFilesDisplay(true)
        }
      } else if (selectedPreset) {
        setChatSettings((prevSettings) => ({
          ...prevSettings,
          model: selectedPreset.model as LLMID,
          prompt: selectedPreset.prompt,
          temperature: selectedPreset.temperature,
          contextLength: selectedPreset.context_length,
          includeProfileContext: selectedPreset.include_profile_context,
          includeWorkspaceInstructions: selectedPreset.include_workspace_instructions,
          embeddingsProvider: selectedPreset.embeddings_provider as 'openai' | 'local'
          // reflectMode: prevSettings.reflectMode
        }))
      } else if (selectedWorkspace) {
        setChatSettings((prevSettings) => ({
          ...prevSettings,
          model: 'losi-omega' as LLMID,
          prompt: newPrompt,
          temperature: selectedWorkspace.default_temperature || 0.5,
          contextLength: selectedWorkspace.default_context_length || 100000,
          includeProfileContext: selectedWorkspace.include_profile_context || true,
          includeWorkspaceInstructions: selectedWorkspace.include_workspace_instructions || true,
          embeddingsProvider:
            (selectedWorkspace.embeddings_provider as 'openai' | 'local') || 'openai'
        }))
      }

      // Only push to route if we're not already there
      if (redirectTo) {
        return router.push(redirectTo)
      } else if (pathname !== '/chat') {
        // Navigate to new chat route
        router.push(`/chat`)
      } else {
        // We're already at /chat route, just reset isNewChat to avoid double EmptyChatView
        // The ChatUI useEffect will handle setting isNewChat to false when chatId is null
      }

      // We don't need to call refreshChats here since we're using atoms now
    } catch (error) {
      toast.error(t('Something went wrong. Please try again.'))
    }
  }

  // Stable identity — ChatInput effects depend on this and call setState.
  // A fresh function each render re-fires those effects → React #185 (max update depth).
  const handleFocusChatInput = useCallback(() => {
    if (!canFocusInput) {
      return
    }
    if (isMobileScreen()) {
      return
    }
    if (shouldPreserveComputerInteractionFocus()) {
      return
    }
    // Don't steal focus from open overlays, dialogs, search panels, etc.
    if (
      typeof document !== 'undefined' &&
      document.querySelector(
        '[role="dialog"], [role="alertdialog"], [role="listbox"], [data-radix-popper-content-wrapper], [data-state="open"][role="menu"], [cmdk-dialog]'
      )
    ) {
      return
    }
    chatInputRef.current?.focus()
  }, [canFocusInput])

  const handleStopMessage = (options?: ChatStopOptions) => {
    if (options?.restoreInput === false) {
      suppressDraftRestoreRef.current = true
    } else {
      suppressDraftRestoreRef.current = false
    }

    const candidateStoreKeys: string[] = []
    const addCandidateStoreKey = (storeKey: string | null) => {
      if (!storeKey) return
      if (candidateStoreKeys.includes(storeKey)) return
      candidateStoreKeys.push(storeKey)
    }

    addCandidateStoreKey(activeRequestChatStoreKeyRef.current)
    addCandidateStoreKey(explicitChatStoreKey)
    addCandidateStoreKey(activeChatKey)

    const candidates = candidateStoreKeys.map((storeKey) => ({
      storeKey,
      bindings: getScopedChatBindings(storeKey)
    }))

    const stopCandidate = (candidate: (typeof candidates)[number]) => {
      const snapshot = candidate.bindings.getSnapshot()
      if (options?.restoreInput === false && pendingChatRequestRef.current) {
        pendingChatRequestRef.current.restoreInputOnAbort = false
      }

      snapshot.abortController?.abort()
      persistStoppedPendingTurn(candidate.bindings)
      clearStoppedPendingTurn(candidate.bindings, options)
      candidate.bindings.setIsGenerating(false)
      candidate.bindings.setIsPersisting(false)
      candidate.bindings.setFirstTokenReceived(false)
      candidate.bindings.setAbortController(null)
      pendingChatRequestRef.current = null
      setToolInUse('none')

      if (options?.restoreInput === false) {
        setUserInput('')
      }

      if (activeRequestChatStoreKeyRef.current === candidate.storeKey) {
        activeRequestChatStoreKeyRef.current = null
        activeRequestStartedAtRef.current = null
      }

      if (!candidate.storeKey.startsWith('draft:')) {
        clearChatActivityStatus(candidate.storeKey)
      }
    }

    for (const candidate of candidates) {
      const snapshot = candidate.bindings.getSnapshot()
      const controller = snapshot.abortController
      if (!controller) continue

      stopCandidate(candidate)
      return
    }

    const activeCandidate = candidates.find((candidate) => {
      const snapshot = candidate.bindings.getSnapshot()
      return snapshot.isGenerating || snapshot.isPersisting
    })

    if (activeCandidate) {
      stopCandidate(activeCandidate)
      return
    }

    if (candidates.length > 0) {
      if (options?.restoreInput === false && pendingChatRequestRef.current) {
        pendingChatRequestRef.current.restoreInputOnAbort = false
      }
      persistStoppedPendingTurn(candidates[0].bindings)
      clearStoppedPendingTurn(candidates[0].bindings, options)
      if (options?.restoreInput === false) {
        setUserInput('')
      }
    }
  }

  const handleSendMessage = async (
    rawMessageContent: string,
    chatMessages: ChatMessage[],
    isRegeneration: boolean,
    options?: ChatSendOptions
  ) => {
    const desktopHandoffAttachments = options?.desktopHandoffAttachments || []
    const forceNewChat = options?.forceNewChat === true && !isRegeneration
    const skipRouteNavigation = options?.skipRouteNavigation === true
    const userMetadata = mergeVoiceMemoMetadata(
      buildDesktopHandoffUserMetadata(desktopHandoffAttachments),
      options?.voiceMemo
    )
    const shouldUseDraftStore =
      forceNewChat ||
      forceDraftChat ||
      (pathname === '/chat' && !normalizedExplicitChatId && !selectedChat)
    const initialChatStoreKey = shouldUseDraftStore
      ? getChatStoreKey(null)
      : explicitChatStoreKey ?? activeChatKey
    let requestChatStoreKey = initialChatStoreKey
    let requestChatBindings = getScopedChatBindings(requestChatStoreKey)
    const initialAttachmentKey = shouldUseDraftStore
      ? scopedDraftAttachmentKey
      : explicitAttachmentKey ?? activeAttachmentKey
    let requestAttachmentKey = initialAttachmentKey
    let requestAttachmentBindings = getScopedAttachmentBindings(
      requestAttachmentKey
    )
    const initialAttachmentState = requestAttachmentBindings.getSnapshot()
    const queuedAttachments = options?.queuedAttachments ?? null
    const shouldIgnoreDraftAttachments =
      Boolean(options?.ignoreDraftAttachments) || isRegeneration
    const shouldUseLiveDraftAttachments =
      !queuedAttachments && !shouldIgnoreDraftAttachments
    const draftImages = shouldUseLiveDraftAttachments
      ? initialAttachmentState.newMessageImages
      : []
    const draftFiles = shouldUseLiveDraftAttachments
      ? initialAttachmentState.newMessageFiles
      : []
    // Read attachments from the scoped store at send time. The visible values
    // can lag behind file-input updates in desktop new-chat drafts.
    const startingImages = mergeMessageImages(
      mergeMessageImages(
        [...(options?.retainedImages ?? [])],
        [...(queuedAttachments?.newMessageImages ?? [])]
      ),
      mergeMessageImages([...draftImages], buildDesktopHandoffImages(desktopHandoffAttachments))
    )
    let startingFiles = mergeChatFiles(
      [...(queuedAttachments?.newMessageFiles ?? [])],
      [...draftFiles]
    )
    const initialGenerationState = requestChatBindings.getSnapshot()
    let persistedPendingAssistantTurn = getLatestPendingAssistantTurnState(
      initialGenerationState.chatMessages
    )

    const hasLocalActiveRequest = isSendLockedByActiveRequest(
      activeRequestChatStoreKeyRef.current,
      requestChatStoreKey
    )

    if (hasLocalActiveRequest) {
      const lockStillActive = hasActiveSendRequestActivity({
        isGenerating: initialGenerationState.isGenerating,
        isPersisting: initialGenerationState.isPersisting,
        startedAt: activeRequestStartedAtRef.current
      })

      if (!lockStillActive) {
        activeRequestChatStoreKeyRef.current = null
        activeRequestStartedAtRef.current = null
        requestChatBindings.setIsGenerating(false)
        requestChatBindings.setIsPersisting(false)
        requestChatBindings.setFirstTokenReceived(false)

        if (initialGenerationState.abortController) {
          requestChatBindings.setAbortController(null)
        }
      } else if (options?.forceAssistantResponse) {
        // Force-send of a queued follow-up: stop the live lock instead of
        // soft-blocking. Never restore the aborted turn's draft into the
        // composer — that ghosted the original message while the force-send
        // started loading.
        suppressDraftRestoreRef.current = true
        if (pendingChatRequestRef.current) {
          pendingChatRequestRef.current.restoreInputOnAbort = false
        }
        initialGenerationState.abortController?.abort()
        activeRequestChatStoreKeyRef.current = null
        activeRequestStartedAtRef.current = null
        requestChatBindings.setIsGenerating(false)
        requestChatBindings.setIsPersisting(false)
        requestChatBindings.setFirstTokenReceived(false)
        requestChatBindings.setAbortController(null)
        setUserInput('')
      } else {
        toast.info('Please wait for the current response to finish.')
        return
      }
    }

    if (persistedPendingAssistantTurn) {
      if (options?.forceAssistantResponse) {
        // Queued force-send must not die behind a leftover pending assistant
        // (common right after Stop). Mark it stopped and continue.
        requestChatBindings.setChatMessages((previous) => {
          const withoutStale = stopStalePendingAssistantTurns(previous)
          const pendingMsgId =
            getLatestPendingAssistantTurnState(withoutStale)?.message.message
              .id ?? persistedPendingAssistantTurn.message.message.id

          return withoutStale.map((chatMessage) => {
            if (chatMessage.message.id !== pendingMsgId) {
              return chatMessage
            }

            return {
              ...chatMessage,
              message: {
                ...chatMessage.message,
                metadata: stopPendingAssistantTurnMetadata(
                  chatMessage.message.metadata
                ) as ChatMessage['message']['metadata']
              }
            }
          })
        })
        requestChatBindings.setIsGenerating(false)
        requestChatBindings.setIsPersisting(false)
        requestChatBindings.setFirstTokenReceived(false)
        requestChatBindings.setAbortController(null)
        persistedPendingAssistantTurn = null
      } else {
        toast.info('Please wait for the current response to finish.')
        return
      }
    }

    if (initialGenerationState.isGenerating) {
      requestChatBindings.setIsGenerating(false)
      requestChatBindings.setFirstTokenReceived(false)
      requestChatBindings.setAbortController(null)
      setToolInUse('none')
      clearChatActivityStatus(requestChatStoreKey)
    }

    if (initialGenerationState.isPersisting && !hasLocalActiveRequest) {
      requestChatBindings.setIsPersisting(false)
    }

    // Check if this is a public chat (no profile means public) - must be defined early
    const isPublic = !profile || pathname === '/try'

    // Don't race a previous turn's post-stream persist on this chat.
    const finalizeWaitChatId =
      !isPublic && !forceNewChat
        ? selectedChat?.id ?? normalizedExplicitChatId ?? null
        : null
    if (finalizeWaitChatId) {
      await waitForChatFinalize(finalizeWaitChatId)
    }

    // Create minimal profile for public users (cast via unknown; full profiles has many more fields)
    const effectiveProfile =
      profile ||
      ({
        user_id: 'public',
        plan: 'free',
        openai_api_key: null,
        anthropic_api_key: null,
        google_generative_ai_api_key: null,
        mistral_api_key: null,
        groq_api_key: null,
        perplexity_api_key: null,
        has_onboarded: false,
        image_provider: 'google',
        image_provider_model: 'gemini-3.1-flash-image',
        embeddings_provider: 'openai',
        model_visibility: {},
        is_admin: false
      } as unknown as Tables<'profiles'>)
    const hasAttachments =
      Boolean(options?.hasAttachments) ||
      startingImages.length > 0 ||
      startingFiles.length > 0 ||
      desktopHandoffAttachments.length > 0
    const tryTempFilesPrompt = isPublic
      ? buildTryTempFilesPromptBlock(
          startingFiles.filter(
            (file) => file.isTemporary || isTryTempFileId(file.id)
          )
        )
      : ''
    // Attachment-only sends used to leave content empty. Retrieval then 400'd
    // on missing userInput, file chunks never reached the model, and Anthropic
    // substitution collapsed the turn to ".". Default to "here" so retrieval
    // runs and the model sees the uploaded document.
    const strippedUserText = stripBlobUrlsFromOutgoingContent(rawMessageContent).trim()
    const attachmentOnlyUserText =
      hasAttachments && !strippedUserText ? 'here' : strippedUserText
    const messageContent = [attachmentOnlyUserText, tryTempFilesPrompt]
      .filter(Boolean)
      .join('\n\n')
    const startingInput = messageContent
    const structuredMentions = mergeStructuredMentions(
      parseStructuredMentions(messageContent),
      options?.structuredMentions
    )
    const mentionedMemberUserIds = getMentionedMemberUserIds(
      structuredMentions,
      effectiveProfile.user_id
    )

    // @-mentioning staffed agents routes the turn into the agent-staffing
    // chat (a direct thread for one agent, a group thread for several) where
    // the agents actually work, instead of a normal LLM turn.
    // Note: parseStructuredMentions normalizes '_' -> '-', so the token type
    // 'staffing_agent' arrives here as 'staffing-agent'. Match both.
    const mentionedStaffingAgentIds = Array.from(
      new Set(
        structuredMentions.items
          .filter((item) => {
            const t = item.type.toLowerCase().replace(/_/g, '-')
            return t === 'staffing-agent'
          })
          .map((item) => item.id)
      )
    )

    if (mentionedStaffingAgentIds.length > 0 && selectedWorkspace) {
      try {
        const res = await fetch('/api/nexus/agent-staffing/mention', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            workspace_id: selectedWorkspace.id,
            agent_ids: mentionedStaffingAgentIds,
            content: stripMentionMarkupForPrompt(messageContent)
          })
        })
        const data = await res.json().catch(() => null)
        if (res.ok && data?.thread?.id) {
          const isGroup = data.thread.kind === 'group'
          toast.success(
            isGroup
              ? 'Sent to your agent group chat'
              : 'Sent to your agent',
            {
              action: {
                label: 'Open',
                onClick: () =>
                  router.push('/nexus/agent-staffing?thread=' + data.thread.id)
              }
            }
          )
        } else {
          toast.error(data?.error ?? "Couldn't reach the agents. Try again.")
        }
      } catch {
        toast.error("Couldn't reach the agents. Try again.")
      }
      // The staffing thread owns this turn; do not also run a normal LLM turn.
      setUserInput('')
      return
    }

    let currentChat =
      forceNewChat || forceDraftChat
        ? null
        : normalizedExplicitChatId
        ? (() => {
            if (selectedChat?.id === normalizedExplicitChatId) {
              return { ...selectedChat }
            }

            const matchingChat = chats.find((chat) => chat.id === normalizedExplicitChatId)
            return matchingChat ? normalizeChatForClient(matchingChat) : null
          })()
        : selectedChat
          ? { ...selectedChat }
          : null
    let createdChatBeforeSend = false
    let pendingCreatedChatRoute: string | null = null
    let openingTitleGenerationStarted = false
    let completedModelRun = false
    let requestRuntimeCleared = false
    let sendLockReleased = false
    let requestAbortController: AbortController | null = null
    let pendingChatRequest: PendingChatRequestState | null = null
    let sendAccepted = false
    let sendRejected = false
    let shouldGenerateAssistantResponse = false
    let persistedUserMessage: Tables<'messages'> | null = null
    let persistedAssistantMessage: Tables<'messages'> | null = null
    const initialPathname =
      typeof window !== 'undefined' ? window.location.pathname : pathname ?? '/chat'
    const requestId = uuidv4()
    const requestStartedAt = Date.now()
    const previousUserMessageCount = chatMessages.filter(
      (chatMessage) => chatMessage.message.role === 'user'
    ).length
    const isFirstActivationUserMessage = !isRegeneration && previousUserMessageCount === 0
    const isSecondActivationUserMessage = !isRegeneration && previousUserMessageCount === 1
    const activationSurface = getActivationSurfaceFromPath(pathname, isPublic)
    const activationSearchParams =
      typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null
    const activationSourceParam = activationSearchParams?.get('source') || ''
    const activationMessageSource: ActivationMessageSource =
      initialPathname === '/try' && activationSourceParam.startsWith('homepage')
        ? 'homepage_ask'
        : initialPathname === '/try' && activationSearchParams?.has('query')
          ? 'url_query'
          : 'composer'
    activeRequestChatStoreKeyRef.current = requestChatStoreKey
    activeRequestStartedAtRef.current = requestStartedAt

    const markSendAccepted = () => {
      if (sendAccepted) return

      sendAccepted = true
      if (!isRegeneration) {
        void trackChatMessageSent({
          message_length: messageContent.trim().length,
          is_first_message: previousUserMessageCount === 0,
          surface: getChatMessageSentSurface(activationSurface)
        })
      }
      if (isFirstActivationUserMessage) {
        void trackChatFirstUserMessageSubmitted({
          surface: activationSurface,
          is_anonymous: isPublic,
          source: activationMessageSource
        })
        if (!isPublic && activationSurface === 'authenticated_chat') {
          void trackFirstChatMessageSent({
            message_length: messageContent.trim().length,
            is_first_message: true,
            surface: 'chat'
          })
        }
      }
      if (isSecondActivationUserMessage) {
        void trackChatSecondUserMessageSubmitted({
          surface: activationSurface,
          is_anonymous: isPublic
        })
      }
      if (options?.onAccepted) {
        options.onAccepted()
        // Always clear the composer — onAccepted (e.g. queue replay) used to
        // return early and leave a ghost draft that race-restored on abort.
        setUserInput('')
        suppressDraftRestoreRef.current = false
        return
      }

      setUserInput('')
      suppressDraftRestoreRef.current = false
    }

    const rejectAcceptedSend = () => {
      if (!sendAccepted || sendRejected || completedModelRun) return false

      sendRejected = true
      if (options?.onRejected) {
        // Always invoke (e.g. re-queue force-sent turns) even when draft
        // restore is suppressed — only the composer restore is gated.
        options.onRejected()
      }
      if (suppressDraftRestoreRef.current) {
        setUserInput('')
      } else if (!options?.onRejected) {
        setUserInput(startingInput)
      }

      return true
    }

    const updateChatActivity = (
      status: 'running' | 'completed' | 'error',
      options?: {
        completedAt?: number
        unread?: boolean
      }
    ) => {
      if (!currentChat?.id || isPublic) return

      const incomingStatus = createChatActivityStatus(
        {
          chatId: currentChat.id,
          requestId,
          status,
          completedAt: options?.completedAt
        },
        { unread: options?.unread }
      )

      const nextRegistry = upsertChatActivityStatus(
        readChatActivityRegistry(),
        incomingStatus
      )

      writeChatActivityRegistry(nextRegistry)
    }

    const releaseRequestSendLock = () => {
      if (sendLockReleased) return

      sendLockReleased = true
      requestChatBindings.setIsPersisting(false)
      pendingChatRequestRef.current = null

      if (
        activeRequestChatStoreKeyRef.current === requestChatStoreKey &&
        activeRequestStartedAtRef.current === requestStartedAt
      ) {
        activeRequestChatStoreKeyRef.current = null
        activeRequestStartedAtRef.current = null
      }
    }

    const clearRequestRuntimeState = (options?: { releaseSendLock?: boolean }) => {
      const releaseSendLock = options?.releaseSendLock ?? true
      const snapshot = requestChatBindings.getSnapshot()
      const ownsActiveRequest =
        activeRequestChatStoreKeyRef.current === requestChatStoreKey &&
        activeRequestStartedAtRef.current === requestStartedAt
      const newerActiveRequest =
        activeRequestChatStoreKeyRef.current === requestChatStoreKey &&
        activeRequestStartedAtRef.current !== null &&
        activeRequestStartedAtRef.current !== requestStartedAt

      if (
        newerActiveRequest ||
        (requestAbortController &&
          snapshot.abortController &&
          snapshot.abortController !== requestAbortController &&
          !ownsActiveRequest)
      ) {
        return
      }

      if (!requestRuntimeCleared) {
        requestRuntimeCleared = true
        requestChatBindings.setIsGenerating(false)
        requestChatBindings.setFirstTokenReceived(false)

        if (
          !requestAbortController ||
          snapshot.abortController === requestAbortController ||
          ownsActiveRequest
        ) {
          requestChatBindings.setAbortController(null)
        }

        setToolInUse('none')
      }

      if (releaseSendLock) {
        releaseRequestSendLock()
      }
    }

    const markPersistedPendingAssistantMessageFailed = async (wasStopped: boolean) => {
      const pendingMessage = persistedAssistantMessage
      if (!pendingMessage) return

      const metadata = wasStopped
        ? stopPendingAssistantTurnMetadata(pendingMessage.metadata)
        : failPendingAssistantTurnMetadata(pendingMessage.metadata)

      try {
        persistedAssistantMessage = await updateMessage(pendingMessage.id, {
          metadata
        })
      } catch (error) {
        safeConsole.warn('Failed to mark pending assistant message failed', error)
      }
    }

    const removePersistedPendingAssistantMessage = async () => {
      const pendingMessage = persistedAssistantMessage
      if (!pendingMessage) return

      try {
        await deleteMessage(pendingMessage.id)
      } catch (error) {
        safeConsole.warn('Failed to delete pending assistant message', error)
        await markPersistedPendingAssistantMessageFailed(false)
      }

      persistedAssistantMessage = null

      requestChatBindings.setChatMessages((previousMessages) =>
        previousMessages.filter((chatMessage) =>
          chatMessage.message.id !== pendingMessage.id &&
          !(
            chatMessage.message.chat_id === pendingMessage.chat_id &&
            chatMessage.message.sequence_number === pendingMessage.sequence_number
          )
        )
      )
    }

    // Declared out here so the catch handler can call it even if we throw
    // before the real implementation is assigned below. Without the default,
    // an early throw hits a TDZ ReferenceError inside catch, masking the
    // real error.
    let restoreDraftAttachments: () => void = () => {}
    // Abort cleanup must scope to THIS request's temp ids (force-send race).
    let abortCleanupUserMessageId: string | null = null
    let abortCleanupAssistantMessageId: string | null = null

    try {
      setIsPromptPickerOpen(false)
      setIsFilePickerOpen(false)
      // Don't clear newMessageImages yet; keep them visible in the UI until upload finishes.
      setSelectedHtmlElements([])

      // Ensure chatSettings exists before proceeding
      // For public users, initialize with defaults if missing
      let effectiveChatSettings: ChatSettings
      if (!chatSettings && isPublic) {
        effectiveChatSettings = {
          model: 'losi-mini' as any,
          prompt: '',
          temperature: 0.7,
          contextLength: 4096,
          includeProfileContext: false,
          includeWorkspaceInstructions: false,
          embeddingsProvider: 'openai' as const,
          reflectMode: false,
          reasoningEffort: 'minimal' as const // gpt-5-mini requires 'minimal' instead of 'none'
        }
        setChatSettings(effectiveChatSettings)
      } else if (!chatSettings) {
        clearRequestRuntimeState()
        toast.error("Chat isn't ready yet. Refresh and try again.")
        return
      } else {
        effectiveChatSettings = chatSettings
      }

      if (options?.chatSettingsOverride) {
        effectiveChatSettings = {
          ...effectiveChatSettings,
          ...options.chatSettingsOverride
        }
      }

      const modelData =
        allModels.find(
          (llm) =>
            llm.modelId === effectiveChatSettings.model ||
            llm.hostedId === effectiveChatSettings.model
        ) ??
        LLM_LIST.find(
          (llm) =>
            llm.modelId === effectiveChatSettings.model ||
            llm.hostedId === effectiveChatSettings.model
        )

      // For public users, if modelData is not found, create a default model data for losi-mini
      let effectiveModelData = modelData
      if (!effectiveModelData && isPublic && effectiveChatSettings.model === 'losi-mini') {
        effectiveModelData = {
          modelId: 'losi-mini' as any,
          modelName: 'Losi Mini',
          provider: 'marshmallow' as any,
          hostedId: 'losi-mini',
          tier: 'free',
          tools: true,
          supportsStreaming: true,
          imageInput: true
        } as any
      }

      // Ensure modelData exists before proceeding
      if (!effectiveModelData) {
        clearRequestRuntimeState()
        toast.error("That model isn't available right now. Refresh and try again.")
        return
      }

      const selectedToolsSnapshot =
        typeof getSelectedToolsSnapshot === 'function'
          ? getSelectedToolsSnapshot()
          : selectedTools

      // Skip validation for public users
      if (!isPublic) {
        validateChatSettings(
          effectiveChatSettings,
          effectiveModelData,
          effectiveProfile,
          selectedWorkspace,
          messageContent,
          selectedAssistant,
          selectedToolsSnapshot,
          hasAttachments
        )
      }

      const desktopHandoffFileAttachments = desktopHandoffAttachments.filter(
        (attachment) => !isDesktopHandoffImage(attachment)
      )
      const attachmentValidation = canUseWorkspaceAttachmentUploadLimits(currentChat)
        ? null
        : validateChatDraftAttachmentUpload(effectiveProfile, {
            newMessageFiles: startingFiles,
            newMessageImages: startingImages,
            extraAttachments: desktopHandoffFileAttachments
          })

      if (attachmentValidation && !attachmentValidation.ok) {
        clearRequestRuntimeState()
        toast.error(attachmentValidation.message)
        scheduleUpgradePaywall()
        return
      }

      markSendAccepted()

      // If the chat belongs to an assistant and tool hydration has not
      // completed yet, fetch the assistant tools before sending so the
      // first turn after open keeps the same tool context as before.
      let effectiveSelectedTools = selectedToolsSnapshot
      if (!isPublic && selectedAssistant && effectiveSelectedTools.length === 0) {
        try {
          effectiveSelectedTools = (await getAssistantToolsByAssistantId(selectedAssistant.id))
            .tools as Tables<'tools'>[]
          setSelectedTools(effectiveSelectedTools)
        } catch (error) {
          safeConsole.warn('Failed to load assistant tools before send:', error)
        }
      }

      if (!isPublic && tools && tools.length > 0) {
        const spacesTool = tools.find(
          (tool) =>
            tool.name === 'Losi Spaces' ||
            tool.id === 'losiSpaces'
        )

        if (spacesTool) {
          const alreadySelected = effectiveSelectedTools.some(
            (tool) =>
              tool.id === spacesTool.id ||
              tool.name === spacesTool.name
          )

          if (!alreadySelected) {
            effectiveSelectedTools = [spacesTool, ...effectiveSelectedTools]
            setSelectedTools(effectiveSelectedTools)
          }
        }
      }

      if (options?.forceWebSearch && tools && tools.length > 0) {
        const webBrowsingTool = tools.find(isWebBrowsingTool)

        if (webBrowsingTool) {
          const alreadySelected = effectiveSelectedTools.some(
            (tool) =>
              tool.id === webBrowsingTool.id ||
              tool.name === webBrowsingTool.name
          )

          if (!alreadySelected) {
            effectiveSelectedTools = [webBrowsingTool, ...effectiveSelectedTools]
          }
        }
      }

      // For public users, ensure Web Browsing tool is selected if available
      if (isPublic && tools && tools.length > 0 && effectiveSelectedTools.length === 0) {
        const webBrowsingTool = tools.find(isWebBrowsingTool)
        if (webBrowsingTool) {
          effectiveSelectedTools = [webBrowsingTool]
          setSelectedTools([webBrowsingTool])
        }
      }

      // Branch behavior: authenticated chats use tools-stream when the model
      // supports selected tools or an explicitly routed Sidecar request.
      const shouldForceSidecarRoute = options?.sidecar?.mode === 'force'
      const shouldUseToolsStream =
        isPublic ||
        Boolean(
          effectiveModelData.tools &&
            (effectiveSelectedTools.length > 0 || shouldForceSidecarRoute || adjacentPaneContext)
        )

      // Client-side precheck for free plan total context cap (~50k tokens).
      // Use cheap char-length estimates — gpt-tokenizer encode() over a long
      // coding chat is sync main-thread work that delays tools-stream start.
      try {
        if (isPublic || isFreePlan(effectiveProfile)) {
          const estimateTokensForContent = (content: unknown): number => {
            if (typeof content === 'string') {
              return Math.ceil(content.length / 4)
            }
            if (Array.isArray(content)) {
              return content.reduce((sum: number, item: unknown) => {
                if (
                  item &&
                  typeof item === 'object' &&
                  (item as { type?: string }).type === 'text' &&
                  typeof (item as { text?: string }).text === 'string'
                ) {
                  return sum + Math.ceil(((item as { text: string }).text).length / 4)
                }
                return sum
              }, 0)
            }
            return 0
          }

          const historicalTokens = chatMessages.reduce((sum: number, cm: ChatMessage) => {
            return sum + estimateTokensForContent(cm.message?.content)
          }, 0)

          const totalTokens = historicalTokens + estimateTokensForContent(messageContent)

          if (totalTokens >= 50000) {
            clearRequestRuntimeState()
            rejectAcceptedSend()
            toast.error('Upgrade to Plus for longer conversations, or start a new chat with summary.')
            scheduleUpgradePaywall()
            return
          }
        }
      } catch {}

      if (normalizedExplicitChatId && !currentChat && !isPublic) {
        const fetchedChat = await fetchAccessibleChatForSend(normalizedExplicitChatId)

        if (fetchedChat) {
          const hydratedChat = normalizeChatForClient(fetchedChat)
          currentChat = hydratedChat
          setSelectedChat(hydratedChat)
          setChats((previousChats) => {
            const chatExists = previousChats.some((chat) => chat.id === hydratedChat.id)

            if (!chatExists) {
              return [hydratedChat, ...previousChats]
            }

            return previousChats.map((chat) =>
              chat.id === hydratedChat.id ? hydratedChat : chat
            )
          })
          addChat({
            chat: hydratedChat,
            viewerUserId: effectiveProfile.user_id
          })
        }
      }

      if (normalizedExplicitChatId && !currentChat && !isPublic) {
        clearRequestRuntimeState()
        rejectAcceptedSend()
        toast.info('This chat is still loading. Please wait a moment and try again.')
        return
      }

      if (desktopHandoffFileAttachments.length > 0) {
        if (isPublic || !selectedWorkspace) {
          throw new Error('Desktop file attachments require an authenticated workspace.')
        }

        const preparedDesktopFiles = await prepareDesktopHandoffFiles(
          desktopHandoffFileAttachments,
          effectiveProfile,
          selectedWorkspace.id,
          effectiveChatSettings.embeddingsProvider
        )

        startingFiles = mergeChatFiles(startingFiles, preparedDesktopFiles)
      }

      if (!currentChat && !isPublic) {
        const createdChat = normalizeChatForClient(
          await handleCreateChat(
            effectiveChatSettings,
            effectiveProfile,
            selectedWorkspace!,
            messageContent,
            selectedAssistant,
            startingFiles,
            effectiveSelectedTools,
            setSelectedChat,
            setChats,
            setVisibleChatFiles,
            setSelectedTools,
            addChat,
            {
              skipGeneratedName: true,
              fallbackName: t('chat_helper.title_generator.default_title')
            }
          )
        )
        currentChat = createdChat
        createdChatBeforeSend = true

        currentChat =
          mentionedMemberUserIds.length > 0
            ? await createMentionOnlyGroupChatFromSource(
                createdChat.id,
                mentionedMemberUserIds,
                getMentionStartedGroupChatName(structuredMentions)
              )
            : createdChat

        if (currentChat?.id) {
          const syncedChat = currentChat
          setSelectedChat(syncedChat)
          setChats((previousChats) =>
            previousChats.map((chat) =>
              chat.id === syncedChat.id ? syncedChat : chat
            )
          )
          updateChatAtomValue(syncedChat)
          requestChatStoreKey = getChatStoreKey(syncedChat.id)
          if (
            !onChatCreate &&
            !skipRouteNavigation &&
            (pathname === '/chat' || forceNewChat)
          ) {
            setPendingNavigationChatId(syncedChat.id)
          } else if (!onChatCreate && skipRouteNavigation) {
            // Bind scoped messages to this chat while staying on /your-crew.
            setActiveChatOverrideId(syncedChat.id)
          }
          migrateChatState(initialChatStoreKey, requestChatStoreKey)
          requestChatBindings = getScopedChatBindings(requestChatStoreKey)
          activeRequestChatStoreKeyRef.current = requestChatStoreKey
          activeRequestStartedAtRef.current = requestStartedAt
          requestAttachmentKey = getAttachmentStoreKey(syncedChat.id)
          migrateAttachmentState(initialAttachmentKey, requestAttachmentKey)
          requestAttachmentBindings = getScopedAttachmentBindings(
            requestAttachmentKey
          )
        }

        if (!onChatCreate && currentChat?.id) {
          pendingCreatedChatRoute = currentChat.id
        }
        if (onChatCreate && currentChat) {
          onChatCreate(currentChat)
        }
      } else if (currentChat?.id) {
        requestChatStoreKey = getChatStoreKey(currentChat.id)
        requestChatBindings = getScopedChatBindings(requestChatStoreKey)
        activeRequestChatStoreKeyRef.current = requestChatStoreKey
        activeRequestStartedAtRef.current = requestStartedAt
        requestAttachmentKey = getAttachmentStoreKey(currentChat.id)
        requestAttachmentBindings = getScopedAttachmentBindings(
          requestAttachmentKey
        )
      }

      if (
        currentChat?.id &&
        currentChat.is_group_chat &&
        !isPublic &&
        !getStoredGroupChatResponseMode(currentChat.metadata)
      ) {
        const fetchedChat = await fetchAccessibleChatForSend(currentChat.id)

        if (fetchedChat) {
          const hydratedChat = normalizeChatForClient(fetchedChat)
          currentChat = hydratedChat
          setSelectedChat(hydratedChat)
          setChats((previousChats) =>
            previousChats.map((chat) =>
              chat.id === hydratedChat.id ? hydratedChat : chat
            )
          )
          updateChatAtomValue(hydratedChat)
        }
      }

      const messageTextForResponseMode = structuredMentions.platformLosiMentioned
        ? `${messageContent}\n@Losi`
        : messageContent
      const shouldSkipStructuredMemberOnlyGroupResponse =
        Boolean(currentChat?.is_group_chat) &&
        structuredMentions.memberUserIds.length > 0 &&
        !structuredMentions.platformLosiMentioned
      const groupChatSkipArgs = {
        isGroupChat: Boolean(currentChat?.is_group_chat),
        metadata: currentChat?.metadata ?? null,
        messageText: messageTextForResponseMode,
        isRegeneration,
        knownHumanMentionTokens: options?.groupMemberMentionTokens,
        knownHumanMentionTokensReady: options?.groupMemberMentionTokensReady
      }
      const shouldForceAssistantResponse = options?.forceAssistantResponse === true
      const shouldSkipAssistantResponse =
        !shouldForceAssistantResponse &&
        (shouldSkipStructuredMemberOnlyGroupResponse ||
          shouldSkipAlwaysModeGroupAssistantForHumanMention(groupChatSkipArgs) ||
          shouldSkipGroupChatAssistantResponse(groupChatSkipArgs))
      shouldGenerateAssistantResponse =
        shouldForceAssistantResponse || !shouldSkipAssistantResponse
      let serverSkippedAssistantResponse = false
      const openingTitleChatId = currentChat?.id

      if (
        createdChatBeforeSend &&
        !openingTitleGenerationStarted &&
        openingTitleChatId &&
        !isRegeneration &&
        !isPublic &&
        shouldGenerateAssistantResponse &&
        shouldScheduleFirstResponseTitleGeneration(currentChat)
      ) {
        openingTitleGenerationStarted = true
        scheduleFirstResponseTitleGeneration({
          chatId: openingTitleChatId,
          userMessage: messageContent,
          assistantMessage: ''
        })
      }

      requestChatBindings.setFirstTokenReceived(false)
      requestChatBindings.setRequestTokensTotal(0)
      requestChatBindings.setResponseTimeToFirstToken(0)
      requestChatBindings.setResponseTimeTotal(0)
      requestChatBindings.setResponseTokensTotal(0)
      const newAbortController = new AbortController()
      requestAbortController = newAbortController
      requestChatBindings.setAbortController(
        shouldGenerateAssistantResponse ? newAbortController : null
      )
      activeRequestChatStoreKeyRef.current = requestChatStoreKey
      activeRequestStartedAtRef.current = requestStartedAt
      if (shouldGenerateAssistantResponse) {
        updateChatActivity('running', { unread: false })
      }

      // Hydrate existing stored images before building prompt image paths.
      for (let i = 0; i < startingImages.length; i++) {
        const img = startingImages[i]
        const existingPath = normalizeMessageImageStorageSource(
          (img as { path?: unknown }).path
        )

        if (existingPath) {
          img.path = existingPath
          void getMessageImageFromStorageWithTimeout(existingPath)
            .then((signedUrl) => {
              img.url = signedUrl
            })
            .catch(() => {})
          continue
        }

        // Fresh composer images are persisted by handleCreateMessages before
        // the user message row is created, so failed uploads cannot fork the
        // text and image paths.
      }

      const promptImagePaths = buildPromptImagePaths(startingImages)
      restoreDraftAttachments = () => {
        if (!shouldUseLiveDraftAttachments) {
          return
        }

        requestAttachmentBindings.setNewMessageFiles(startingFiles)
        requestAttachmentBindings.setNewMessageImages(startingImages)
        requestAttachmentBindings.setChatImages((previousImages) =>
          previousImages.filter(
            (image) =>
              !startingImages.some(
                (startingImage) =>
                  startingImage.messageId === image.messageId &&
                  startingImage.url === image.url &&
                  startingImage.path === image.path
              )
          )
        )

        if (startingFiles.length > 0 || startingImages.length > 0) {
          requestAttachmentBindings.setShowFilesDisplay(true)
        }
      }

      // Move uploaded images into the scoped chat image store, then clear the
      // composer images for the next message in this same chat only.
      requestAttachmentBindings.setChatImages((prev) => [
        ...prev,
        ...startingImages
      ])
      if (shouldUseLiveDraftAttachments) {
        requestAttachmentBindings.setNewMessageFiles([])
        requestAttachmentBindings.setNewMessageImages([])
      }

      let retrievedFileItems: Tables<'file_items'>[] = []
      const shouldUseRetrieval = useRetrieval || startingFiles.length > 0
      if (startingFiles.length > 0 && !useRetrieval) {
        setUseRetrieval(true)
      }

      // Skip retrieval for public users (requires authentication)
      if (
        !isPublic &&
        shouldGenerateAssistantResponse &&
        (startingFiles.length > 0 || visibleChatFiles.length > 0) &&
        shouldUseRetrieval
      ) {
        setToolInUse('retrieval')

        retrievedFileItems = await handleRetrieval(
          messageContent,
          startingFiles,
          visibleChatFiles,
          effectiveChatSettings.embeddingsProvider,
          sourceCount
        )
      }

      const structuredMentionAnnotation =
        getStructuredMentionAnnotation(structuredMentions)
      const userAnnotations = [
        ...(selectedHtmlElements?.length > 0
          ? [{ selected_html_elements: selectedHtmlElements }]
          : []),
        ...(structuredMentionAnnotation
          ? [{ structured_mentions: structuredMentionAnnotation }]
          : [])
      ]
      let assistantAnnotations: Json | null = null
      const showFreeLosiMaxFallbackNotice = (annotation: Json | null) => {
        if (!annotation || typeof annotation !== 'object' || Array.isArray(annotation)) return

        const modelSwitch = (annotation as Record<string, unknown>).model_switch
        if (!modelSwitch || typeof modelSwitch !== 'object' || Array.isArray(modelSwitch)) return

        const record = modelSwitch as Record<string, unknown>
        if (record.reason !== 'free_plan_losi_max' || record.to !== 'losi-mini') return

        setChatSettings((prev) => ({ ...prev, model: 'losi-mini' as LLMID }))
        showFreeLosiMaxLimitToast({
          details: readFreeLosiMaxLimitDetails(record),
          onSkipWait: () => setIsPaywallOpen(true)
        })
      }

      // Set isGenerating before creating temp messages to ensure UI updates immediately
      pendingChatRequest = shouldGenerateAssistantResponse
        ? {
            input: startingInput,
            hasOutput: false,
            restoreInputOnAbort: true
          }
        : null
      pendingChatRequestRef.current = pendingChatRequest
      requestChatBindings.setIsGenerating(shouldGenerateAssistantResponse)
      // Warm extensions + memories while we persist user/pending rows so
      // handleToolsChat does not pay that latency after DB writes finish.
      if (shouldGenerateAssistantResponse && shouldUseToolsStream && !isPublic) {
        warmToolsChatSendCaches({
          chatId: currentChat?.id ?? normalizedExplicitChatId,
          messageContent,
          userId: effectiveProfile?.user_id ?? null
        })
      }
      const setRequestFirstTokenReceived: React.Dispatch<
        React.SetStateAction<boolean>
      > = (value) => {
        requestChatBindings.setFirstTokenReceived((previous) => {
          const nextValue =
            typeof value === 'function'
              ? (value as (previous: boolean) => boolean)(previous)
              : value

          if (nextValue && pendingChatRequest) {
            pendingChatRequest.hasOutput = true
            if (pendingChatRequestRef.current === pendingChatRequest) {
              pendingChatRequestRef.current = pendingChatRequest
            }
          }

          return nextValue
        })
      }

      const tempMessages = createTempMessages(
        messageContent,
        chatMessages,
        effectiveChatSettings,
        promptImagePaths,
        isRegeneration,
        requestChatBindings.setChatMessages,
        selectedAssistant,
        userAnnotations,
        currentChat?.id ?? normalizedExplicitChatId,
        effectiveProfile?.user_id ?? null,
        shouldGenerateAssistantResponse,
        userMetadata,
        shouldGenerateAssistantResponse
          ? buildPendingAssistantTurnMetadata(requestStartedAt)
          : null
      )
      const tempUserChatMessage = tempMessages.tempUserChatMessage
      let tempAssistantChatMessage = tempMessages.tempAssistantChatMessage
      let requestUserChatMessage = tempUserChatMessage
      abortCleanupUserMessageId = requestUserChatMessage.message.id
      abortCleanupAssistantMessageId = shouldGenerateAssistantResponse
        ? tempAssistantChatMessage.message.id
        : null
      let modelImages = bindImagesToMessage(
        startingImages,
        tempUserChatMessage.message.id,
        promptImagePaths
      )

      const snapshotChatFileItems = requestChatBindings.getSnapshot().chatFileItems
      const payloadChatFileItems =
        snapshotChatFileItems.length > 0 ? snapshotChatFileItems : visibleChatFileItems
      const linkedSpaceContext = buildLinkedSpaceChatContext(currentChat)

      const buildSendPayload = (
        userMessage: ChatMessage,
        pendingAssistantId?: string | null
      ): ChatPayload => ({
        chatSettings: effectiveChatSettings,
        selectedTools: effectiveSelectedTools,
        chatMessages: (isRegeneration
          ? [...chatMessages]
          : [...chatMessages, userMessage]
        ).map(reconstructContentWithCodeBlocksInChatMessage),
        assistant: selectedAssistant,
        messageFileItems: retrievedFileItems,
        chatFileItems: payloadChatFileItems,
        messageHtmlElements: selectedHtmlElements,
        ...(structuredMentions.items.length > 0
          ? { structuredMentions }
          : {}),
        ...(isPublic ? { isPublic: true } : {}),
        ...(currentChat?.id ? { chatId: currentChat.id } : {}),
        ...(pendingAssistantId
          ? { pendingAssistantMessageId: pendingAssistantId }
          : {}),
        ...(selectedWorkspace?.id ? { workspaceId: selectedWorkspace.id } : {}),
        // Send the user's real browser timezone so the system prompt's
        // {local_date} reflects their local calendar even without a workspace
        // timezone. Guard the Intl lookup for non-browser/edge environments.
        ...(() => {
          try {
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
            return tz ? { clientTimeZone: tz } : {}
          } catch {
            return {}
          }
        })(),
        ...(linkedSpaceContext ? { linked_space_context: linkedSpaceContext } : {}),
        ...(adjacentPaneContext ? { adjacent_pane_context: adjacentPaneContext } : {}),
        ...(currentChat?.is_group_chat
          ? { groupChat: { isGroupChat: true } }
          : {}),
        ...(options?.addon ? { addon: options.addon } : {}),
        ...(options?.sidecar ? { sidecar: options.sidecar } : {}),
        ...(options?.crewMemberId
          ? { crewMemberId: options.crewMemberId }
          : {}),
        ...(options?.crewRoster ? { crewRoster: options.crewRoster } : {}),
        ...(pathname?.startsWith('/nexus') && selectedWorkspace
          ? {
              sidekick_context: {
                route: 'nexus',
                pathname: pathname ?? '',
                workspaceId: selectedWorkspace.id
              }
            }
          : {})
      })

      // Overlap prompt build with user+pending persist. Fetch still waits for
      // pendingAssistantMessageId (quota / tool-run / group loader contract).
      let preparedToolsRequestPromise: Promise<PreparedToolsChatRequest> | null =
        null

      if (shouldGenerateAssistantResponse && shouldUseToolsStream) {
        preparedToolsRequestPromise = prepareToolsChatRequest({
          payload: buildSendPayload(tempUserChatMessage),
          profile: effectiveProfile,
          chatImages: [...visibleChatImages, ...modelImages],
          selectedTools: effectiveSelectedTools,
          isRegeneration,
          isThinkDeeperEnabled,
          allTools: tools,
          tempAssistantChatMessage,
          setChatMessages: requestChatBindings.setChatMessages
        })
      }

      if (!isPublic && currentChat && !isRegeneration) {
        requestChatBindings.setIsPersisting(true)

        try {
          persistedUserMessage = await handleCreateMessages(
            chatMessages,
            currentChat,
            effectiveProfile,
            effectiveModelData,
            messageContent,
            '',
            startingImages,
            false,
            [],
            requestChatBindings.setChatMessages,
            requestChatBindings.setChatFileItems,
            requestAttachmentBindings.setChatImages,
            selectedAssistant,
            userAnnotations,
            null,
            null,
            null,
            null,
            null,
            null,
            {
              persistAssistantMessage: false,
              userMetadata,
              onCreatedUserMessage: (message) => {
                if (!currentChat?.is_group_chat) {
                  return
                }

                dispatchGroupMentionNotifications(currentChat.id, message.id)
              }
            }
          )
          const persistedImagePaths = Array.isArray(persistedUserMessage?.image_paths)
            ? persistedUserMessage.image_paths.filter(Boolean)
            : []
          const requestImagePaths = mergePersistedPromptImagePaths(
            persistedImagePaths,
            promptImagePaths,
            startingImages.length
          )

          if (persistedUserMessage) {
            requestUserChatMessage = {
              ...tempUserChatMessage,
              message: {
                ...tempUserChatMessage.message,
                id: persistedUserMessage.id,
                image_paths: requestImagePaths
              }
            }
            abortCleanupUserMessageId = persistedUserMessage.id
            modelImages = bindImagesToMessage(
              startingImages,
              persistedUserMessage.id,
              requestImagePaths
            )
          }

          if (shouldGenerateAssistantResponse && persistedUserMessage) {
            try {
              // Persist the running assistant row before generation so other
              // group members see the working loader through realtime too.
              const pendingAssistantMessage = await createPendingAssistantMessage({
                currentChat,
                profile: effectiveProfile,
                modelData: effectiveModelData,
                selectedAssistant,
                sequenceNumber: persistedUserMessage.sequence_number + 1,
                startedAt: requestStartedAt
              })
              persistedAssistantMessage = pendingAssistantMessage
              tempAssistantChatMessage = {
                ...tempAssistantChatMessage,
                message: {
                  ...tempAssistantChatMessage.message,
                  ...pendingAssistantMessage,
                  metadata: pendingAssistantMessage.metadata ?? null,
                  type: pendingAssistantMessage.type ?? null
                }
              }
              abortCleanupAssistantMessageId = pendingAssistantMessage.id
              requestChatBindings.setChatMessages((previousMessages) =>
                previousMessages.map((chatMessage) => {
                  const isPendingAssistantSlot =
                    chatMessage.message.role === 'assistant' &&
                    chatMessage.message.chat_id === pendingAssistantMessage.chat_id &&
                    chatMessage.message.sequence_number ===
                      pendingAssistantMessage.sequence_number

                  return isPendingAssistantSlot
                    ? tempAssistantChatMessage
                    : chatMessage
                })
              )
            } catch (error) {
              safeConsole.warn('Failed to persist pending assistant message', error)
            }
          }

          if (pendingCreatedChatRoute && !skipRouteNavigation) {
            router.push(`/chat/${pendingCreatedChatRoute}`)
            pendingCreatedChatRoute = null
          } else if (pendingCreatedChatRoute && skipRouteNavigation) {
            // Stay on /your-crew (or current route); bind the scoped store to
            // the new chat so the inline Crew thread keeps streaming here.
            setActiveChatOverrideId(pendingCreatedChatRoute)
            pendingCreatedChatRoute = null
          }
        } catch (error) {
          requestChatBindings.setChatMessages(chatMessages)
          throw error
        } finally {
          requestChatBindings.setIsPersisting(false)
        }
      }

      const payload: ChatPayload = buildSendPayload(
        requestUserChatMessage,
        persistedAssistantMessage?.id
      )

      let generatedText = ''

      if (shouldGenerateAssistantResponse) {
        if (isFirstActivationUserMessage) {
          void trackChatFirstAiResponseStarted({
            surface: activationSurface,
            is_anonymous: isPublic
          })
        }

        try {
          if (shouldUseToolsStream) {
            const finalChatImages = [...visibleChatImages, ...modelImages]
            const finalImageFingerprint =
              buildChatImagePathFingerprint(finalChatImages)

            let preparedToolsRequest: PreparedToolsChatRequest | null = null
            if (preparedToolsRequestPromise) {
              try {
                preparedToolsRequest = await preparedToolsRequestPromise
              } catch (error) {
                safeConsole.warn(
                  'Overlapped tools prompt prepare failed; rebuilding after persist',
                  error
                )
                preparedToolsRequest = null
              }
            }

            if (
              !preparedToolsRequest ||
              preparedToolsRequest.imagePathFingerprint !== finalImageFingerprint
            ) {
              preparedToolsRequest = await prepareToolsChatRequest({
                payload,
                profile: effectiveProfile,
                chatImages: finalChatImages,
                selectedTools: effectiveSelectedTools,
                isRegeneration,
                isThinkDeeperEnabled,
                allTools: tools,
                tempAssistantChatMessage,
                setChatMessages: requestChatBindings.setChatMessages
              })
            }

            const toolsChatResult = await handleToolsChat(
              payload,
              effectiveProfile,
              tempAssistantChatMessage,
              isRegeneration,
              newAbortController,
              finalChatImages,
              requestChatBindings.setIsGenerating,
              setRequestFirstTokenReceived,
              requestChatBindings.setChatMessages,
              setToolInUse,
              effectiveSelectedTools,
              effectiveModelData.supportsStreaming ?? true,
              requestChatBindings.setResponseTimeToFirstToken,
              requestChatBindings.setResponseTimeTotal,
              requestChatBindings.setResponseTokensTotal,
              requestChatBindings.setRequestTokensTotal,
              isThinkDeeperEnabled,
              tools,
              (newModelId: string) => {
                setChatSettings((prev) => ({ ...prev, model: newModelId as LLMID }))
              },
              undefined,
              preparedToolsRequest
            )
            generatedText = toolsChatResult.generatedText
            const streamedAnnotations =
              toolsChatResult.data &&
              typeof toolsChatResult.data === 'object' &&
              !Array.isArray(toolsChatResult.data)
                ? (toolsChatResult.data as Json)
                : null
            assistantAnnotations = resolveAssistantAnnotationForPersist({
              chatMessages: requestChatBindings.getSnapshot().chatMessages,
              tempAssistantMessage: tempAssistantChatMessage.message,
              streamedAnnotation: streamedAnnotations
            })
            throwAbortErrorIfNeeded(newAbortController.signal)
          } else if (effectiveModelData.provider === 'ollama') {
            const localChatResult = await handleLocalChat(
              payload,
              effectiveProfile,
              effectiveChatSettings,
              tempAssistantChatMessage,
              isRegeneration,
              newAbortController,
              requestChatBindings.setIsGenerating,
              setRequestFirstTokenReceived,
              requestChatBindings.setChatMessages,
              setToolInUse,
              (newModelId: string) => {
                setChatSettings((prev) => ({ ...prev, model: newModelId as LLMID }))
              }
            )
            generatedText = localChatResult.generatedText
            const streamedAnnotations =
              localChatResult.data &&
              typeof localChatResult.data === 'object' &&
              !Array.isArray(localChatResult.data)
                ? (localChatResult.data as Json)
                : null
            assistantAnnotations = resolveAssistantAnnotationForPersist({
              chatMessages: requestChatBindings.getSnapshot().chatMessages,
              tempAssistantMessage: tempAssistantChatMessage.message,
              streamedAnnotation: streamedAnnotations
            })
            throwAbortErrorIfNeeded(newAbortController.signal)
          } else {
            ;({ generatedText } = await handleHostedChat(
              payload,
              effectiveProfile,
              effectiveModelData,
              tempAssistantChatMessage,
              isRegeneration,
              newAbortController,
              modelImages,
              [...visibleChatImages, ...modelImages],
              requestChatBindings.setIsGenerating,
              setRequestFirstTokenReceived,
              requestChatBindings.setChatMessages,
              setToolInUse,
              undefined,
              undefined,
              undefined,
              undefined,
              advancedVoiceMode,
              (newModelId: string) => {
                setChatSettings((prev) => ({ ...prev, model: newModelId as LLMID }))
              }
            ))
            throwAbortErrorIfNeeded(newAbortController.signal)
          }
        } catch (error) {
          if (!isGroupChatMentionOnlyConflictError(error)) {
            throw error
          }

          serverSkippedAssistantResponse = true
          generatedText = ''
          assistantAnnotations = null
        }
      }

      showFreeLosiMaxFallbackNotice(assistantAnnotations)

      // Only persist if this request has not been aborted.
      throwAbortErrorIfNeeded(newAbortController.signal)
      if (
        shouldGenerateAssistantResponse &&
        !serverSkippedAssistantResponse &&
        !hasAssistantResponsePayload(generatedText, assistantAnnotations)
      ) {
        serverSkippedAssistantResponse = true
        requestChatBindings.setChatMessages((previousMessages) => {
          const lastMessage = previousMessages[previousMessages.length - 1]

          if (hasAssistantTurnOutput(lastMessage)) {
            return previousMessages
          }

          return lastMessage?.message.role === 'assistant'
            ? previousMessages.slice(0, -1)
            : previousMessages
        })
      }
      completedModelRun =
        shouldGenerateAssistantResponse && !serverSkippedAssistantResponse
      if (serverSkippedAssistantResponse) {
        await removePersistedPendingAssistantMessage()
      }
      if (isFirstActivationUserMessage && completedModelRun) {
        void trackChatFirstAiResponseSucceeded({
          surface: activationSurface,
          is_anonymous: isPublic,
          latency_ms: Date.now() - requestStartedAt
        })
      }
      if (completedModelRun) {
        // Stream is done — mark terminal locally + with a tiny DB metadata write
        // before unlock so reload/other tabs don't see a zombie running turn.
        // Heavy content finalize still runs below without holding the composer.
        const assistantMessageId =
          persistedAssistantMessage?.id || tempAssistantChatMessage.message.id
        const sourceMetadata =
          (assistantMessageId
            ? requestChatBindings
                .getSnapshot()
                .chatMessages.find(
                  chatMessage => chatMessage.message.id === assistantMessageId
                )?.message.metadata
            : null) ?? tempAssistantChatMessage.message.metadata
        const completedMetadata = completePendingAssistantTurnMetadata(
          sourceMetadata
        ) as ChatMessage['message']['metadata']

        if (assistantMessageId) {
          tempAssistantChatMessage = {
            ...tempAssistantChatMessage,
            message: {
              ...tempAssistantChatMessage.message,
              metadata: completedMetadata
            }
          }
          requestChatBindings.setChatMessages((previousMessages) =>
            previousMessages.map((chatMessage) => {
              if (chatMessage.message.id !== assistantMessageId) {
                return chatMessage
              }

              return {
                ...chatMessage,
                message: {
                  ...chatMessage.message,
                  metadata: completedMetadata
                }
              }
            })
          )
        }

        if (!isPublic && persistedAssistantMessage?.id) {
          try {
            const liveAssistantMessage =
              requestChatBindings
                .getSnapshot()
                .chatMessages.find(
                  chatMessage => chatMessage.message.id === assistantMessageId
                )?.message ?? null
            const liveContent =
              typeof liveAssistantMessage?.content === 'string'
                ? liveAssistantMessage.content
                : typeof generatedText === 'string'
                  ? generatedText
                  : ''
            const liveAnnotation = resolveAssistantAnnotationForPersist({
              chatMessages: requestChatBindings.getSnapshot().chatMessages,
              tempAssistantMessage: tempAssistantChatMessage.message,
              streamedAnnotation: assistantAnnotations
            })

            // Flush content + tool annotations with the early completed marker.
            // Metadata-only unlock used to leave empty rows when heavy finalize
            // was aborted by force-send / reload (images lived only in live UI).
            persistedAssistantMessage = await updateMessage(
              persistedAssistantMessage.id,
              {
                metadata: completedMetadata as Json,
                content: liveContent,
                annotation: liveAnnotation
              }
            )
          } catch (error) {
            safeConsole.warn(
              'Failed to persist terminal assistant streamStatus before unlock',
              error
            )
          }
        }
      }
      clearRequestRuntimeState()

      const releaseChatFinalize =
        !isPublic && currentChat?.id
          ? beginChatFinalize(currentChat.id)
          : null

      try {
      // Extract session_id from response headers
      // const session_id = newAbortController.signal.headers?.get("X-Session-ID") || null
      // Skip chat creation for public users (they use sessionStorage instead)
      if (!currentChat && isPublic) {
        // Create a temporary chat object for public users (sessionStorage only)
        // Use persistent sessionChatId from sessionStorage
        let persistentChatId = `public_${Date.now()}`
        if (typeof window !== 'undefined') {
          const storedId = sessionStorage.getItem('public_chat_session_id')
          if (storedId) {
            persistentChatId = storedId
          } else {
            sessionStorage.setItem('public_chat_session_id', persistentChatId)
          }
        }
        currentChat = {
          id: persistentChatId,
          user_id: 'public',
          workspace_id: '',
          name: 'New Chat',
          model: effectiveChatSettings.model,
          prompt: effectiveChatSettings.prompt || '',
          temperature: effectiveChatSettings.temperature || 0.7,
          context_length: effectiveChatSettings.contextLength || 4096,
          include_profile_context: effectiveChatSettings.includeProfileContext || false,
          include_workspace_instructions:
            effectiveChatSettings.includeWorkspaceInstructions || false,
          embeddings_provider: effectiveChatSettings.embeddingsProvider || 'openai',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          share_path: null,
          assistant_id: selectedAssistant?.id || null,
          metadata: null,
          type: null
        } as any
      } else if (!isPublic) {
        // Only update database chats for authenticated users
        if (!currentChat) throw new Error('Failed to create or update chat')

        const chat = currentChat as NonNullable<typeof currentChat>
        // Persist the model actually used for this send onto the chat row —
        // without this, a later forced reload (e.g. the realtime-refresh
        // effect once isGenerating/isPersisting clears) re-syncs
        // chatSettings.model from the DB's stale, never-updated value and
        // the picker visibly reverts to whatever model the chat started with.
        const updatedChat = await touchChatActivity(
          chat.id,
          undefined,
          undefined,
          effectiveChatSettings.model
        )
        throwAbortErrorIfNeeded(newAbortController.signal)
        const defaultChatTitle = t('chat_helper.title_generator.default_title')
        const generatedOpeningTitle =
          generatedOpeningChatTitleByIdRef.current.get(chat.id)
        const buildActivityTouchedChat = (
          localChat: Tables<'chats'> | null | undefined
        ): Tables<'chats'> => {
          const localName = localChat?.name?.trim()
          const touchedName = updatedChat.name?.trim()
          const preservedName =
            (generatedOpeningTitle?.trim() && touchedName === defaultChatTitle
              ? generatedOpeningTitle
              : null) ||
            (localName && localName !== touchedName && touchedName === defaultChatTitle
              ? localChat.name
              : updatedChat.name)

          return {
            ...updatedChat,
            name: preservedName,
            metadata: updatedChat.metadata ?? localChat?.metadata ?? chat.metadata ?? null,
            type: updatedChat.type ?? localChat?.type ?? chat.type ?? null
          }
        }
        const localChatSnapshot =
          (selectedChat?.id === updatedChat.id ? selectedChat : null) ??
          chats.find((candidate) => candidate.id === updatedChat.id) ??
          chat
        const activityTouchedChat = buildActivityTouchedChat(localChatSnapshot)

        setChats((prevChats) => {
          const updatedChats = prevChats.map((prevChat) =>
            prevChat.id === updatedChat.id
              ? buildActivityTouchedChat(prevChat)
              : prevChat
          )

          return updatedChats
        })

        // Also update the atom with the full chat object
        updateChatAtomValue(activityTouchedChat)

        if (onChatUpdate) {
          onChatUpdate(activityTouchedChat)
        }
      }

      if (currentChat) {
        const currentChatId = currentChat.id

        // For public users, save to sessionStorage instead of database
        if (isPublic) {
          // Use persistent sessionChatId from sessionStorage or currentChat.id
          let sessionChatId = currentChat.id
          if (!sessionChatId && typeof window !== 'undefined') {
            const storedId = sessionStorage.getItem('public_chat_session_id')
            if (storedId) {
              sessionChatId = storedId
            } else {
              sessionChatId = `public_${Date.now()}`
              sessionStorage.setItem('public_chat_session_id', sessionChatId)
            }
          } else if (!sessionChatId) {
            sessionChatId = `public_${Date.now()}`
          }

          // Save messages to sessionStorage
          // IMPORTANT: Use generatedText to update assistant message content
          // The handlers update chatMessages via setChatMessages during streaming,
          // but we need to ensure the assistant message has the full content
          // Parse tap-to-reply markers before saving assistant content.
          const {
            mergeAssistantResponseControlMetadata,
            parseSuggestions
          } = await import('@/lib/utils/parse-suggestions')
          const rawContent = generatedText || tempAssistantChatMessage.message.content
          const parsedResponseControls = parseSuggestions(rawContent)
          const cleanedContent = sanitizeAssistantVisibleContent(parsedResponseControls.content)
          const assistantAnnotation = resolveAssistantAnnotationForPersist({
            chatMessages: requestChatBindings.getSnapshot().chatMessages,
            tempAssistantMessage: tempAssistantChatMessage.message,
            streamedAnnotation: assistantAnnotations
          })
          const { applyThisTurnClaimGuard } = await import(
            '@/lib/chat/this-turn-claim-guard'
          )
          const claimGuarded = applyThisTurnClaimGuard({
            content: cleanedContent,
            annotation: assistantAnnotation
          })
          const guardedContent = claimGuarded.content

          const currentMetadata = tempAssistantChatMessage.message.metadata as Record<
            string,
            unknown
          > | null
          const mergedMetadata = mergeAssistantResponseControlMetadata(
            currentMetadata,
            parsedResponseControls
          ) as Json | null
          const newMetadata = completePendingAssistantTurnMetadata(mergedMetadata)
          const shouldSaveAssistantResponse =
            completedModelRun &&
            hasAssistantResponsePayload(
              guardedContent,
              assistantAnnotation as Json | null,
              newMetadata
            )

          const updatedAssistantMessage: ChatMessage = {
            ...tempAssistantChatMessage,
            message: {
              ...tempAssistantChatMessage.message,
              content: guardedContent,
              annotation: assistantAnnotation,
              metadata: newMetadata
            }
          }

          const updatedMessages = [...chatMessages]
          if (!isRegeneration) {
            // Check if user message already exists (might have been added during streaming)
            const hasUserMessage = updatedMessages.some(
              (m) => m.message.id === tempUserChatMessage.message.id
            )
            if (!hasUserMessage) {
              updatedMessages.push(tempUserChatMessage)
            }
          }
          const assistantMessageIndex = updatedMessages.findIndex(
            (m) => m.message.id === tempAssistantChatMessage.message.id
          )

          if (shouldSaveAssistantResponse) {
            // Check if assistant message already exists and update it, otherwise add it
            if (assistantMessageIndex >= 0) {
              updatedMessages[assistantMessageIndex] = updatedAssistantMessage
            } else {
              updatedMessages.push(updatedAssistantMessage)
            }
          } else if (assistantMessageIndex >= 0) {
            updatedMessages.splice(assistantMessageIndex, 1)
          }

          // Import and use session storage utilities
          throwAbortErrorIfNeeded(newAbortController.signal)
          const { saveChatToSession } = await import('@/lib/storage/public-chat-storage')
          throwAbortErrorIfNeeded(newAbortController.signal)
          saveChatToSession(sessionChatId, updatedMessages)
          // The authenticated path's handleCreateMessages merges the
          // finalized (streamStatus: 'completed') message into live state;
          // without this, the public path's assistant message stays stuck
          // with the 'running' metadata set at turn start, so
          // getLatestPendingAssistantTurnState keeps treating it as an
          // active turn — isGenerating never clears and every later send on
          // this page gets silently queued instead of sent.
          requestChatBindings.setChatMessages(updatedMessages)

          if (shouldSaveAssistantResponse) {
            options?.onResponsePersisted?.()
          }
        } else {
          throwAbortErrorIfNeeded(newAbortController.signal)
          const { applyThisTurnClaimGuard } = await import(
            '@/lib/chat/this-turn-claim-guard'
          )
          const persistAnnotation = resolveAssistantAnnotationForPersist({
            chatMessages: requestChatBindings.getSnapshot().chatMessages,
            tempAssistantMessage: tempAssistantChatMessage.message,
            streamedAnnotation: assistantAnnotations
          })
          const claimGuardedPersist = applyThisTurnClaimGuard({
            content: generatedText || '',
            annotation: persistAnnotation
          })
          await handleCreateMessages(
            chatMessages,
            currentChat,
            effectiveProfile,
            effectiveModelData,
            messageContent,
            claimGuardedPersist.content || generatedText,
            persistedUserMessage ? [] : startingImages,
            isRegeneration,
            retrievedFileItems,
            requestChatBindings.setChatMessages,
            requestChatBindings.setChatFileItems,
            requestAttachmentBindings.setChatImages,
            selectedAssistant,
            userAnnotations,
            persistAnnotation ?? assistantAnnotations,
            null,
            null,
            null,
            (tempAssistantChatMessage.message.metadata ?? null) as Json | null,
            null,
            {
              existingUserMessage: persistedUserMessage,
              existingAssistantMessage: persistedAssistantMessage,
              persistAssistantMessage: shouldGenerateAssistantResponse
                && !serverSkippedAssistantResponse,
              skipPersistedMessagesFetch: Boolean(
                persistedUserMessage?.id && persistedAssistantMessage?.id
              ),
              userMetadata,
              onCreatedUserMessage: (message) => {
                if (!currentChat?.is_group_chat) {
                  return
                }

                dispatchGroupMentionNotifications(currentChat.id, message.id)
              }
            }
          )

          if (shouldGenerateAssistantResponse && !serverSkippedAssistantResponse) {
            options?.onResponsePersisted?.()
          }

          // Optimistically update chat list without full refresh (only for authenticated users)
          if (!isPublic) {
            const updatedTimestamp = new Date().toISOString()
            setChats((prevChats) =>
              prevChats.map((chat) =>
                chat.id === currentChatId ? { ...chat, updated_at: updatedTimestamp } : chat
              )
            )

            // No atom write here: the touch step above already updated the
            // atom with the DB row (generated title preserved). Re-writing
            // from `currentChat` would clobber that title — it is the object
            // captured at send time, when the name was still "New Chat" —
            // and the sidebar syncs the atom back over the chat list/header.
          }

          // @Assistant mentions = reply-as participants (no context swap).
          const mentionedAssistantIds = structuredMentions?.assistantIds || []
          if (
            !isPublic &&
            currentChatId &&
            mentionedAssistantIds.length > 0 &&
            selectedWorkspace?.id
          ) {
            try {
              const latestSequence =
                Math.max(
                  0,
                  ...requestChatBindings
                    .getSnapshot()
                    .chatMessages.map(m => m.message.sequence_number || 0)
                ) + 1
              const participantRes = await authenticatedFetch(
                '/api/assistants/participant-reply',
                {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    assistantIds: mentionedAssistantIds,
                    message: stripMentionMarkupForPrompt(messageContent),
                    chatId: currentChatId,
                    workspaceId: selectedWorkspace.id,
                    sequenceNumber: latestSequence
                  })
                }
              )
              const participantData = await participantRes.json().catch(() => null)
              const allReplies = Array.isArray(participantData?.replies)
                ? participantData.replies
                : []
              const okReplies = allReplies.filter((r: any) => r?.ok && r?.message)
              const failedReplies = allReplies.filter((r: any) => !r?.ok)
              if (okReplies.length > 0) {
                requestChatBindings.setChatMessages(previous => [
                  ...previous,
                  ...okReplies.map((reply: any) => ({
                    fileItems: [],
                    message: reply.message
                  }))
                ])
              }
              if (!participantRes.ok) {
                toast.error(
                  participantData?.error ||
                    'Mentioned Assistants couldn’t reply. Try again in a moment.'
                )
              } else if (okReplies.length === 0 && mentionedAssistantIds.length > 0) {
                const names = failedReplies
                  .map((r: any) => r?.assistantName || r?.error)
                  .filter(Boolean)
                  .slice(0, 3)
                toast.error(
                  names.length
                    ? `Assistant reply failed: ${names.join(', ')}`
                    : 'Mentioned Assistants didn’t reply. Check access and try again.'
                )
              } else if (failedReplies.length > 0) {
                toast.warning(
                  `${failedReplies.length} Assistant${failedReplies.length === 1 ? '' : 's'} couldn’t reply.`
                )
              }
            } catch (participantError) {
              console.error('Assistant participant replies failed:', participantError)
              toast.error(
                'Mentioned Assistants couldn’t reply. Try again in a moment.'
              )
            }
          }
        }
      }

      if (shouldGenerateAssistantResponse && !serverSkippedAssistantResponse) {
        updateChatActivity('completed', {
          completedAt: Date.now(),
          unread:
            typeof window !== 'undefined' &&
            Boolean(currentChat?.id) &&
            window.location.pathname !== `/chat/${currentChat?.id}`
        })
      } else if (currentChat?.id && !isPublic) {
        clearChatActivityStatus(currentChat.id, { requestId })
      }
      clearRequestRuntimeState()

      // setUserInput("")
      } finally {
        releaseChatFinalize?.()
      }
    } catch (error: unknown) {
      const wasAborted = isAbortLikeError(error)
      const exceptionSeverity = completedModelRun ? 'warning' : 'error'
      const toastErrorMessage = getErrorMessage(error)

      if (
        isFirstActivationUserMessage &&
        shouldGenerateAssistantResponse &&
        !completedModelRun
      ) {
        void trackChatFirstAiResponseFailed({
          surface: activationSurface,
          is_anonymous: isPublic,
          error_type: getActivationErrorType(error),
          stage: 'model_response'
        })
      }

      // On user-initiated stop (wasAborted), keep the just-created chat so
      // an immediate follow-up send doesn't race delete → guard-403. Only
      // tear down a newly-created chat when the model failed to start at all
      // and no user action is likely to reuse it.
      if (
        !wasAborted &&
        createdChatBeforeSend &&
        currentChat?.id &&
        !isPublic &&
        !completedModelRun
      ) {
        try {
          await deleteChatInDB(currentChat.id)
          clearChatActivityStatus(currentChat.id, { requestId })
          setChats((prevChats) => prevChats.filter((chat) => chat.id !== currentChat?.id))
          const draftChatBindings = getScopedChatBindings(initialChatStoreKey)
          migrateAttachmentState(requestAttachmentKey, initialAttachmentKey)
          requestAttachmentBindings = getScopedAttachmentBindings(
            initialAttachmentKey
          )
          setSelectedChat(null)
          setPendingNavigationChatId(null)
          draftChatBindings.setChatMessages(visibleChatMessages)
          draftChatBindings.setChatFileItems(visibleChatFileItems)
          draftChatBindings.setAbortController(null)
          draftChatBindings.setIsGenerating(false)
          draftChatBindings.setIsPersisting(false)
          draftChatBindings.setFirstTokenReceived(false)
          if (
            typeof window !== 'undefined' &&
            window.location.pathname === `/chat/${currentChat.id}`
          ) {
            router.replace(initialPathname)
          }
        } catch (cleanupError) {}
      }

      if (currentChat?.id && !isPublic && (!createdChatBeforeSend || completedModelRun)) {
        if (wasAborted) {
          clearChatActivityStatus(currentChat.id, { requestId })
        } else {
          updateChatActivity('error', {
            completedAt: Date.now(),
            unread:
              typeof window !== 'undefined' &&
              window.location.pathname !== `/chat/${currentChat.id}`
          })
        }
      }

      if (error instanceof SubscriptionRequiredError) {
        scheduleUpgradePaywall()
      }

      if (wasAborted) {
        await removePersistedPendingAssistantMessage()

        // Scope cleanup to THIS request's temp ids. An unscoped tail cleanup
        // races force-send: the newer user+assistant pair is already at the
        // end, and stripping it ghosts that draft back into the composer.
        const abortCleanup = getRequestScopedPendingTurnCleanup(
          requestChatBindings.getSnapshot().chatMessages,
          {
            userMessageId: abortCleanupUserMessageId,
            assistantMessageId: abortCleanupAssistantMessageId
          }
        )
        if (abortCleanup.removedPendingAssistantTurn) {
          requestChatBindings.setChatMessages(abortCleanup.nextMessages)
        }

        const pendingRequest = pendingChatRequest
        // Only put the draft back in the composer when we actually removed the
        // pending turn. If the assistant already had tools/text, the user
        // message stays in-thread — restoring would duplicate it on re-send.
        // Force-send / stop-without-restore must never bounce a draft back.
        const shouldRestoreStoppedDraft =
          !suppressDraftRestoreRef.current &&
          pendingRequest?.restoreInputOnAbort !== false &&
          abortCleanup.removedPendingAssistantTurn

        if (shouldRestoreStoppedDraft) {
          // Belt-and-suspenders: if the user turn is still in the thread,
          // restoring the draft duplicates it on the next send.
          const stillHasUserTurn = abortCleanup.nextMessages.some(
            (chatMessage) =>
              chatMessage.message.role === 'user' &&
              typeof chatMessage.message.content === 'string' &&
              chatMessage.message.content ===
                (abortCleanup.restoredUserInput ??
                  pendingRequest?.input ??
                  startingInput)
          )

          if (!stillHasUserTurn) {
            if (!rejectAcceptedSend()) {
              setUserInput(
                abortCleanup.restoredUserInput ??
                  pendingRequest?.input ??
                  startingInput
              )
            }
            restoreDraftAttachments()
          }
        } else if (
          suppressDraftRestoreRef.current ||
          pendingRequest?.restoreInputOnAbort === false
        ) {
          // Force-send / explicit stop: keep the composer empty even if
          // rejectAcceptedSend would otherwise put the aborted draft back.
          setUserInput('')
        }
      } else if (completedModelRun) {
        await markPersistedPendingAssistantMessageFailed(false)

        requestChatBindings.setChatMessages((previous) => {
          return getPendingAssistantTurnCleanup(previous).nextMessages
        })
      } else {
        await removePersistedPendingAssistantMessage()
      }

      clearRequestRuntimeState()

      if (!wasAborted) {
        const isSubscriptionRequired = error instanceof SubscriptionRequiredError
        const isFirstPublicResponse =
          isPublic &&
          !chatMessages.some((chatMessage) => chatMessage.message.role === 'assistant')

        if (!isSubscriptionRequired) {
          if (isFirstPublicResponse) {
            capturePublicChatFirstResponseException(error, {
              source: 'chat-send',
              stage: completedModelRun ? 'first_response_save' : 'first_response',
              severity: exceptionSeverity,
              path: initialPathname,
              route: pathname ?? initialPathname
            })
          } else {
            captureClientException(error, {
              source: 'chat-send',
              activationSurface: currentChat?.is_group_chat
                ? 'shared_chat'
                : initialPathname.startsWith('/spaces')
                  ? 'spaces'
                  : 'chat',
              activationStage: completedModelRun ? 'response_save' : 'send_message',
              severity: exceptionSeverity,
              path: initialPathname,
              route: pathname ?? initialPathname,
              userId: isPublic ? null : effectiveProfile.user_id
            })
          }

          if (completedModelRun) {
            toast.error(
              toastErrorMessage
                ? `Your reply finished, but it couldn't be saved. ${toastErrorMessage}`
                : "Your reply finished, but it couldn't be saved. Try again in a moment."
            )
          } else {
            // Persistent until dismissed, with one-tap Retry: a transient
            // toast alone left failed sends easy to miss (and the failure
            // invisible in the thread). The draft is also restored to the
            // composer below; Retry resubmits it directly. The send
            // pipeline dedupes an identical tail user message, so a retry
            // after a partially-persisted turn cannot double-post.
            toast.error(
              toastErrorMessage
                ? `Couldn't send that message. ${toastErrorMessage}`
                : "Couldn't send that message. Try again.",
              startingInput?.trim()
                ? {
                    duration: 15_000,
                    action: {
                      label: 'Retry',
                      onClick: () => {
                        void handleSendMessageRef.current(
                          startingInput,
                          requestChatBindings.getSnapshot().chatMessages,
                          false
                        )
                      }
                    }
                  }
                : { duration: 15_000 }
            )
          }
        }

        if (!completedModelRun) {
          if (!rejectAcceptedSend()) {
            setUserInput(startingInput)
          }
          restoreDraftAttachments()
        }
      }
    } finally {
      // Always clear this request's runtime state. The ownership guard
      // inside clearRequestRuntimeState can early-return if a newer request
      // has taken over, leaving isGenerating/isPersisting stuck on ("ghost
      // response" bug). Force-clear here: if a newer request IS active, it
      // will re-set isGenerating(true) on its own — this just ensures the
      // stale request never leaves the UI in a loading state.
      clearRequestRuntimeState()

      if (!requestRuntimeCleared) {
        requestRuntimeCleared = true
        requestChatBindings.setIsGenerating(false)
        requestChatBindings.setIsPersisting(false)
        requestChatBindings.setFirstTokenReceived(false)
        setToolInUse('none')
      }
    }
  }

  // Listen for signposting location events and auto-send follow-up message
  const signpostingMessagesRef = useRef(visibleChatMessages)
  signpostingMessagesRef.current = visibleChatMessages
  const handleSendMessageRef = useRef(handleSendMessage)
  handleSendMessageRef.current = handleSendMessage

  useEffect(() => {
    const handleLocationReceived = (event: Event) => {
      const { latitude, longitude, chatId } = (
        event as CustomEvent
      ).detail as {
        latitude: number
        longitude: number
        chatId: string
        savePreference: boolean
      }

      if (!chatId) return

      // Module-level dedup — only first handler to see this key sends
      const key = `${chatId}:${latitude.toFixed(4)},${longitude.toFixed(4)}`
      if (_signpostingSentKeys.has(key)) return
      _signpostingSentKeys.add(key)

      const locationMessage = `My location is: ${latitude}, ${longitude}`

      setTimeout(() => {
        handleSendMessageRef.current(
          locationMessage,
          signpostingMessagesRef.current,
          false
        )
      }, 300)
    }

    window.addEventListener(
      SIGNPOSTING_LOCATION_EVENT,
      handleLocationReceived
    )

    const handleCredentialOutcome = (event: Event) => {
      const detail = (event as CustomEvent).detail as {
        chatId: string
        domain: string
        status: 'saved' | 'declined'
      }

      if (!detail?.chatId || !detail.domain) return

      const key = `${detail.chatId}:credential:${detail.status}:${detail.domain}`
      if (_signpostingSentKeys.has(key)) return
      _signpostingSentKeys.add(key)

      const message =
        detail.status === 'saved'
          ? `Credential saved for ${detail.domain}`
          : `Credential request declined for ${detail.domain}`

      setTimeout(() => {
        handleSendMessageRef.current(
          message,
          signpostingMessagesRef.current,
          false
        )
      }, 300)
    }

    window.addEventListener(
      COMPUTER_USE_CREDENTIAL_EVENT,
      handleCredentialOutcome
    )

    const handleConnectorConnected = (event: Event) => {
      const detail = (event as CustomEvent).detail as ConnectorConnectedEventDetail

      if (!detail?.chatId || !detail.connector) return

      const key = `${detail.chatId}:connector:${detail.status}:${detail.connector}`
      if (_signpostingSentKeys.has(key)) return
      _signpostingSentKeys.add(key)

      const message = connectorAckMessage(detail)

      setTimeout(() => {
        handleSendMessageRef.current(
          message,
          signpostingMessagesRef.current,
          false
        )
      }, 300)
    }

    window.addEventListener(CONNECTOR_CONNECTED_EVENT, handleConnectorConnected)

    return () => {
      window.removeEventListener(
        SIGNPOSTING_LOCATION_EVENT,
        handleLocationReceived
      )
      window.removeEventListener(
        COMPUTER_USE_CREDENTIAL_EVENT,
        handleCredentialOutcome
      )
      window.removeEventListener(
        CONNECTOR_CONNECTED_EVENT,
        handleConnectorConnected
      )
    }
  }, [])

  const handleSendEdit = async (editedContent: string, sequenceNumber: number) => {
    const editableChat =
      normalizedExplicitChatId
        ? selectedChat?.id === normalizedExplicitChatId
          ? selectedChat
          : chats.find((chat) => chat.id === normalizedExplicitChatId) ?? null
        : selectedChat

    if (!editableChat) return

    // Editing mid-stream used to leave the old user turn in the thread while
    // the draft bounced back — stop without restoring, then replace from the
    // edited sequence onward.
    const editStoreKey = getChatStoreKey(editableChat.id)
    const editBindings = getScopedChatBindings(editStoreKey)
    const editSnapshot = editBindings.getSnapshot()
    if (editSnapshot.isGenerating || editSnapshot.abortController) {
      handleStopMessage({ restoreInput: false })
    }

    const deleteResult = await deleteMessagesIncludingAndAfter(
      editableChat.user_id,
      editableChat.id,
      sequenceNumber
    )

    if (deleteResult && typeof deleteResult === 'object' && 'error' in deleteResult) {
      throw new Error(deleteResult.error)
    }

    const editedChatMessage = visibleChatMessages.find(
      (chatMessage) =>
        chatMessage.message.sequence_number === sequenceNumber &&
        chatMessage.message.role === 'user'
    )
    const retainedImages = buildRetainedMessageImages(editedChatMessage?.message)

    const filteredMessages = visibleChatMessages.filter(
      (chatMessage) => chatMessage.message.sequence_number < sequenceNumber
    )

    // Sort filtered messages to ensure proper order before sending
    const sortedFilteredMessages = [...filteredMessages].sort(
      (a, b) => a.message.sequence_number - b.message.sequence_number
    )

    // Write to the scoped store synchronously so createTempMessages cannot
    // race against a stale React snapshot that still holds the edited turn.
    editBindings.setChatMessages(sortedFilteredMessages)
    setVisibleChatMessages(sortedFilteredMessages)

    await handleSendMessage(editedContent, sortedFilteredMessages, false, {
      retainedImages,
      ignoreDraftAttachments: true,
      forceAssistantResponse: true
    })
  }

  function parseApplicationDetails(message: string) {
    // Implement logic to parse application details from the message
    // This is a simplified example
    const [, name, description] =
      message.match(/\/create application\s+"([^"]+)"\s+"([^"]+)"/) || []
    return { name, description }
  }

  const { selectedCodeBlock, handleSelectCodeBlock, handleCodeChange, isEditable } =
    useCodeBlockManager(visibleChatMessages)

  return {
    chatInputRef,
    // prompt,
    handleNewChat,
    handleSendMessage,
    handleFocusChatInput,
    handleStopMessage,
    handleSendEdit,
    selectedCodeBlock,
    handleSelectCodeBlock,
    handleCodeChange,
    isEditable,
    advancedVoiceMode,
    handleStartAdvancedVoiceMode,
    handleConfirmAdvancedVoiceMode,
    handleEndAdvancedVoiceMode,
    onTranscriptUpdate,
    onRealtimeInferenceEvent,
    responseTimeToFirstToken,
    responseTimeTotal,
    responseTokensTotal,
    requestTokensTotal
  }
}
